import { useRole } from '../hooks/useRole';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import ExportButton from './header/ExportButton';
import { VenuesListBar } from './header/VenuesBar';
import { EventsListBar, EventDetailBar } from './header/EventsBar';
import { OrdersListBar, OrderDetailBar, ShipmentsListBar, ShipmentDetailBar, AgentsListBar, AgentDetailBar, InventoryBar } from './header/ProcurementBars';
import AsoebiOrdersBar from './asoebi/AsoebiOrdersBar';
import { AssignmentsListBar, AssignmentEditorBar } from './asoebiAssignments/AssignmentsBarActions';
import { VendorBookingsListBar, VendorBookingEditBar } from './vendorBookings/BookingsBars';
import { GiftRegistryListBar, GiftItemBar } from './giftRegistry/GiftRegistryBars';
import { giftRefFromPath, giftRefLabel } from '../utils/giftRegistry';
import { isAsoebiOrdersListUrl } from '../utils/asoebiOrdersRoute';
import { useProcurementBar } from '../hooks/useProcurementBar';
import { useEventsBar } from '../hooks/useEventsBar';
import IconButton from './ui/IconButton';
import SplitButton from './ui/SplitButton';
import { QuotesFilters, PaymentsFilters, DecorFilters } from './header/BarFilters';
import ArchivedToggle from './ui/ArchivedToggle';
import { isArchiveView, toArchiveView, type ArchiveView } from '../utils/archiveFilter';
import { useLocation, useNavigate } from 'react-router-dom';
import { GuestsListBar } from './header/GuestsBar';
import { matchGuestsRoute } from '../utils/guestRoutes';
import { Calendar, Zap, LayoutList, Layers, Clock, Maximize2, Minimize2 } from 'lucide-react';
import Icon from './Icon';
import { entityPath } from '../utils/entityRoutes';
import { parseIdFromPath } from '../utils/jiraId';
import { recordLabel, recordLabelOr } from '../utils/recordId';
import { useRecordRefs } from '../utils/recordRefs';
import SearchableSelect from './ui/SearchableSelect';
import TaskFilters, { DEFAULT_TASK_STATUS_FILTER } from './header/TaskFilters';
import SyncBadge from './SyncBadge';
import '../styles/components/Header.css';
import { useNotifications } from './NotificationProvider';
import GlobalSearch from './GlobalSearch';
import NotificationDropdown from './NotificationDropdown';
import AuthButton from './AuthButton';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import DemoBadge from './DemoBadge';
import ActionMenu, { ActionMenuItem } from './ActionMenu';
import BreadcrumbBar, { BreadcrumbAction } from './BreadcrumbBar';
import CommunicationsBar from './communications/CommunicationsBar';
import TeamBar from './team/TeamBar';
import GlobalRefreshButton from './header/GlobalRefreshButton';
import RecordNav from './header/RecordNav';
import { WAITER_MENU_TABS_EVENT, WAITER_MENU_TAB_SET_EVENT, type WaiterMenuTabsState } from './header/waiterMenuTabs';
import HeaderStatusSteps, { type HeaderStep } from './header/HeaderStatusSteps';
import { runPageRefresh } from '../hooks/usePageRefresh';
import { loadVendorCategories, refreshVendorCategoriesFromApi } from '../services/VendorCategoryService';
import { loadEvents } from '../services/EventsService';
import { MENU_TABS } from '../services/MenuService';
import {
  BUDGET_CATEGORY_TABS,
  BUDGET_CATEGORY_DROPDOWN_TABS,
  PAYMENTS_TABS,
  LOGISTICS_TABS,
  EVENT_DAY_TABS,
  tabLabel,
} from '../constants/tabDefinitions';
import { loadAssigneeOptions } from '../services/TasksService';
import { useProject } from '../contexts/ProjectContext';
import { useMoney } from '../hooks/useMoney';

interface BreadcrumbItem {
  label: string;
  view: string;
  eventId?: string;
  onAction?: () => void;
}

interface HeaderProps {
  onSuggestVendor: () => void;
  lastScrapeTime: string | null;
  activeView: string;
  onViewChange: (view: string, options?: { eventId?: string | null }) => void;
  timelineView?: 'gantt' | 'sequence' | 'run-of-show';
  onTimelineViewChange?: (view: 'gantt' | 'sequence' | 'run-of-show') => void;
  onAddEvent?: () => void;
  onAddVehicle?: () => void;
  driverId?: string | null;
  driverName?: string | null;
  vehicleId?: string | null;
  vehicleName?: string | null;
  currentTaskId?: string | null;
  taskName?: string | null;
  decorElementCategory?: string | null;
  decorElementId?: string | null;
  decorElementName?: string | null;
  vendorId?: string | null;
  vendorName?: string | null;
  vendorProfileId?: string | null;
  vendorProfileName?: string | null;
  vendorRatingId?: string | null;
  vendorRatingName?: string | null;
  stationeryItemId?: string | null;
  stationeryItemName?: string | null;
  stationeryItemArchived?: boolean;
  taskArchived?: boolean;
  catalogueItemId?: string | null;
  catalogueItemName?: string | null;
  menuItemName?: string | null;
  menuItemId?: string | null;
  guidelineId?: string | null;
  guidelineName?: string | null;
  budgetCategoryId?: string | null;
  budgetCategoryName?: string | null;
  onContactVendor?: () => void;
  onRateVendorAction?: () => void;
  onSaveVendor?: () => void;
  onDeleteVendor?: () => void;
  onFormSave?: () => void;
  onFormDelete?: () => void;
  onAddTask?: () => void;
  activityView?: 'list' | 'kanban' | 'assignee';
  onActivityViewChange?: (view: 'list' | 'kanban' | 'assignee') => void;
  activityFilter?: string;
  onActivityFilterChange?: (tab: string) => void;
  myTasksOnly?: boolean;
  onMyTasksToggle?: () => void;
  priorityFilters?: string[];
  onPriorityFiltersChange?: (priorities: string[]) => void;
  assigneeFilters?: string[];
  onAssigneeFiltersChange?: (assignees: string[]) => void;
  taskStatusFilter?: ArchiveView;
  onTaskStatusFilterChange?: (filter: ArchiveView) => void;
  taskWorkflowStatusFilter?: string[];
  onTaskWorkflowStatusFilterChange?: (statuses: string[]) => void;
  onAddItem?: () => void;
  onAddSong?: () => void;
  onSongSaveClick?: () => void;
  onSongCancelClick?: () => void;
  songFormCanSave?: boolean;
  paymentFilterStatus?: string;
  onPaymentFilterStatusChange?: (status: string) => void;
  paymentSortField?: string;
  onPaymentSortFieldChange?: (field: string) => void;
  paymentsTab?: 'payments' | 'contributors' | 'settlement';
  onPaymentsTabChange?: (tab: 'payments' | 'contributors' | 'settlement') => void;
  quoteDetailId?: string;
  stationeryCategory?: string;
  onStationeryCategoryChange?: (cat: string) => void;
  stationeryArchived?: boolean;
  onStationeryArchivedChange?: (on: boolean) => void;
  stationeryView?: 'list' | 'kanban';
  onStationeryViewChange?: (view: 'list' | 'kanban') => void;
  musicTab?: string;
  onMusicTabChange?: (tab: string) => void;
  onAddGuideline?: () => void;
  menuTab?: string;
  onMenuTabChange?: (tab: string) => void;
  menuView?: 'card' | 'list';
  onMenuViewChange?: (view: 'card' | 'list') => void;
  eventsTab?: string;
  onEventsTabChange?: (tab: string) => void;
  vendorView?: 'table' | 'cards';
  onVendorViewChange?: (view: 'table' | 'cards') => void;
  vendorStatusFilter?: 'active' | 'archived';
  onVendorStatusFilterChange?: (filter: 'active' | 'archived') => void;
  venueStatusFilter?: ArchiveView;
  onVenueStatusFilterChange?: (filter: ArchiveView) => void;
  vendorCategoryFilter?: string;
  onVendorCategoryFilterChange?: (category: string) => void;
  budgetEventFilter?: string;
  onBudgetEventFilterChange?: (id: string) => void;
  budgetEvents?: { id: string; title: string }[];
  paymentsEventFilter?: string;
  onPaymentsEventFilterChange?: (id: string) => void;
  budgetView?: 'cards' | 'table';
  onBudgetViewChange?: (view: 'cards' | 'table') => void;
  onAddBudgetCategory?: () => void;
  onDeleteBudgetCategory?: () => void;
  budgetCategoryTab?: 'overview' | 'vendors' | 'purchases' | 'payments';
  onBudgetCategoryTabChange?: (tab: 'overview' | 'vendors' | 'purchases' | 'payments') => void;
  onAddBudgetVendor?: () => void;
  onSaveBudgetCategory?: () => void;
  isEditingBudgetVendor?: boolean;
  onSaveBudgetVendor?: () => void;
  onCancelBudgetVendor?: () => void;
  logisticsTab?: string;
  onLogisticsTabChange?: (tab: string) => void;
  onAddDriver?: () => void;
  onAddAssignment?: () => void;
  logisticsAssignmentFormOpen?: boolean;
  logisticsAssignmentIsEditing?: boolean;
  assignmentFormStatus?: string;
  onAssignmentFormStatusChange?: (s: string) => void;
  onCancelAssignment?: () => void;
  rosEventFilter?: string;
  onRosEventFilterChange?: (title: string) => void;
  rosEventTitles?: string[];
  onAutomationToggle?: () => void;
  onVendorRefresh?: () => void;
  calendarViewMode?: 'week' | 'month';
  onCalendarViewModeChange?: (mode: 'week' | 'month') => void;
  calendarLabel?: string;
  onCalendarPrev?: () => void;
  onCalendarNext?: () => void;
  onCalendarToday?: () => void;
  onGlobalRefresh?: () => Promise<void>;
  isSyncing?: boolean;
  onSaveSettings?: () => void;
  onResetSettings?: () => void;
  settingsHasChanges?: boolean;
  settingsSaving?: boolean;
  eventList?: { id: string; title: string }[];
  onEventNavigate?: (id: string) => void;
  vendorList?: { id: string; name: string }[];
  onVendorNavigate?: (id: string) => void;
  edTab?: string;
  onEdTabChange?: (tab: string) => void;
  edEventId?: string | null;
  onEdEventChange?: (id: string) => void;
  edEvents?: { id: string; title: string; venue?: string }[];
  edWaiterMode?: 'orders' | 'assignments';
  onEdWaiterModeChange?: (mode: 'orders' | 'assignments') => void;
  onAddDecorElement?: () => void;
  decorCategoryFilter?: string;
  onDecorCategoryFilterChange?: (cat: string) => void;
  decorStatusFilter?: string;
  onDecorStatusFilterChange?: (status: string) => void;
  decorEventFilter?: string;
  onDecorEventFilterChange?: (eventId: string) => void;
  decorEvents?: { id: string; title: string }[];
  onMobileMenuToggle?: () => void;
  edKbViewMode?: 'kanban' | 'batch';
  onEdKbViewModeChange?: (m: 'kanban' | 'batch') => void;
  edPhase?: number;
  onEdPhaseChange?: (p: number) => void;
  edActiveTab?: string;
}

// Map each view to its icon name (matching Sidebar navItems)
const VIEW_ICONS: Record<string, string> = {
  guests: 'users',
  dashboard: 'chart',
  communications: 'inbox',
  team: 'users',
  'team-member-form': 'users',
  vendors: 'briefcase',
  budget: 'wallet',
  'budget-variances': 'alert',
  asoebi: 'sparkles',
  'asoebi-fabrics': 'sparkles',
  'asoebi-orders': 'sparkles',
  'asoebi-assignments': 'package',
  'vendor-bookings': 'briefcase',
  'gift-registry': 'gift',
  'gift-registry-new': 'plus',
  'gift-registry-item': 'gift',
  'vendor-booking-edit': 'briefcase',
  'asoebi-bundles': 'sparkles',
  logistics: 'mapPin',
  procurement: 'globe',
  'procurement-orders': 'package',
  'procurement-order-new': 'plus',
  'procurement-order-edit': 'package',
  'procurement-shipments': 'truck',
  'procurement-shipment-new': 'plus',
  'procurement-shipment-edit': 'truck',
  timeline: 'timeline',
  menu: 'utensils',
  music: 'music',
  stationery: 'document',
  activities: 'check',
  settings: 'settings',
  payments: 'creditCard',
  calendar: 'calendar',
  shipping: 'globe',
  venue: 'mapPin',
  'event-day': 'activity',
  couple: 'ring',
  'venue-new': 'plus',
  'venue-detail': 'mapPin',
  'vendor-detail': 'briefcase',
  'budget-category': 'wallet',
  calculator: 'wallet',
  global: 'chart',
  'task-new': 'plus',
  'inbox-new': 'plus',
  'task-edit': 'check',
  'vehicle-new': 'mapPin',
  'vehicle-detail': 'mapPin',
  'stationery-item-new': 'plus',
  'stationery-item-edit': 'document',
  'budget-category-new': 'plus',
  'song-new': 'plus',
  'driver-new': 'plus',
  'menu-item-new': 'plus',
  'menu-item-edit': 'utensils',
  events: 'calendar',
  'event-new': 'plus',
  'event-detail': 'calendar',
  guidelines: 'list',
  'guideline-new': 'plus',
  'guideline-edit': 'list',
  'calendar-event-new': 'plus',
  'calendar-event-edit': 'calendar',
  decor: 'sparkles',
  'vendor-suggestion': 'plus',
  'vendor-profile': 'briefcase',
  'vendor-rating': 'star',
  'driver-detail': 'mapPin',
  quotes: 'document',
  history: 'clock',
  catalogue: 'grid',
  'catalogue-new': 'plus',
  'catalogue-edit': 'grid',
};

const VIEW_TITLES: Record<string, string> = {
  dashboard: 'Dashboard',
  guests: 'Guests',
  communications: 'Communications',
  team: 'Team',
  'team-member-form': 'Team member',
  vendors: 'Vendors',
  budget: 'Budget',
  'budget-variances': 'Budget Variances',
  asoebi: 'Asoebi & Style',
  'asoebi-fabrics': 'Fabrics',
  'asoebi-orders': 'Orders',
  'asoebi-assignments': 'Assignments',
  'vendor-bookings': 'Vendor Bookings',
  'gift-registry': 'Gift Registry',
  'gift-registry-new': 'New Gift Registry Item',
  'gift-registry-item': 'Gift Registry Item',
  'vendor-booking-edit': 'Vendor Booking',
  'asoebi-bundles': 'Bundles',
  logistics: 'Logistics',
  procurement: 'Inventory',
  'procurement-orders': 'Procurement Orders',
  'procurement-order-new': 'New Order',
  'procurement-order-edit': 'Order',
  'procurement-shipments': 'Shipments',
  'procurement-shipment-new': 'New Shipment',
  'procurement-shipment-edit': 'Shipment',
  timeline: 'Event Day Timeline',
  menu: 'Menu',
  music: 'Music',
  stationery: 'Stationery & Branding',
  'stationery-item-edit': 'Edit Stationery Item',
  activities: 'Tasks',
  settings: 'Settings',
  payments: 'Payments',
  calendar: 'Calendar',
  shipping: 'Shipping Agents',
  venue: 'Venues & Floor Plans',
  'venue-detail': 'Venue & Floor Plan',
  'vendor-detail': 'Vendor Detail',
  'budget-category': 'Budget Category',
  calculator: 'Budget Configurator',
  global: 'Add New Item',
  'event-day': 'Event Day Hub',
  'task-new': 'New Task',
  'task-edit': 'Task',
  'inbox-new': 'New Message',
  'vehicle-new': 'New Vehicle',
  'vehicle-detail': 'Vehicle Detail',
  'stationery-item-new': 'New Stationery Item',
  'budget-category-new': 'New Budget Category',
  'song-new': 'New Song',
  'driver-new': 'New Driver',
  'menu-item-new': 'New Menu Item',
  'menu-item-edit': 'Menu Item',
  events: 'Events',
  'event-new': 'New Event',
  'event-detail': 'Event',
  guidelines: 'Guidelines',
  'guideline-new': 'New Guideline',
  'guideline-edit': 'Edit Guideline',
  'calendar-event-new': 'New Calendar Event',
  'calendar-event-edit': 'Edit Calendar Event',
  decor: 'Decor Planner',
  'decor-new': 'Add Element',
  'decor-edit': 'Edit Element',
  couple: 'Couple Profile',
  projects: 'Projects',
  pipeline: 'Sales Pipeline',
  'vendor-suggestion': 'New Vendor',
  'vendor-profile': 'Vendor Profile',
  'vendor-rating': 'Rate Vendor',
  'driver-detail': 'Driver Details',
  quotes: 'Quotes',
  history: 'History',
  'shipping-agent-edit': 'Agent',
  catalogue: 'Catalogue',
  'catalogue-new': 'New Item',
  'catalogue-edit': 'Edit Item',
};

// System status popover — Network is the one signal Header already tracks
// (the event-day socket connection). Call Hubs / Watches come from the
// ESP32 hub registry (real waiter call-button + smartwatch hardware);
// there's no POS terminal, printer, or backup system in this app's domain,
// so this only shows what's actually backed by data.
function SystemStatusPopover({ connected }: { connected: boolean | null }) {
  const [hubs, setHubs] = useState<Array<{ hub_id: string; watch_macs: string[]; online: boolean }> | null>(null);

  useEffect(() => {
    import('../services/EventDayApiClient').then(({ eventDayApi }) =>
      eventDayApi.getHubs().then(d => setHubs(d.hubs)).catch(() => setHubs([]))
    );
  }, []);

  const hubsOnline = hubs?.filter(h => h.online).length ?? 0;
  const hubsTotal = hubs?.length ?? 0;
  const watchCount = new Set(hubs?.flatMap(h => h.watch_macs) ?? []).size;

  return (
    <div className="ed-system-status-popover">
      <div className="ed-system-status-title">System Status</div>
      <div className="ed-system-status-row">
        <span><span className={`ed-dot ${connected ? 'ed-dot-success' : 'ed-dot-danger'}`} /> Network</span>
        <span>{connected === null ? 'Connecting…' : connected ? 'Live' : 'Offline'}</span>
      </div>
      {hubs !== null && hubs.length > 0 && (
        <>
          <div className="ed-system-status-row">
            <span><span className={`ed-dot ${hubsOnline === hubsTotal ? 'ed-dot-success' : hubsOnline > 0 ? 'ed-dot-warning' : 'ed-dot-danger'}`} /> Call Hubs</span>
            <span>{hubsOnline}/{hubsTotal} online</span>
          </div>
          <div className="ed-system-status-row">
            <span><span className="ed-dot ed-dot-success" /> Watches</span>
            <span>{watchCount} registered</span>
          </div>
        </>
      )}
      {hubs !== null && hubs.length === 0 && (
        <div className="ed-system-status-row ed-system-status-empty">No call hubs registered yet</div>
      )}
    </div>
  );
}

// Shared by every page whose "Show Archived" now lives in this breadcrumb
// instead of floating in the page body (Decor/Logistics/Menu/Music/
// Contributor) — a plain hook rather than repeating the same state +
// two-way CustomEvent sync five times. The page itself still owns the real
// filter value; this only mirrors it so the dropdown can render here.
// eventPrefix + '-sync' is dispatched BY the page (mount + on change),
// eventPrefix + '-set' is dispatched BY this dropdown's onChange.
function useArchiveFilterSync(eventPrefix: string): [ArchiveView, (v: ArchiveView) => void] {
  const [value, setValue] = React.useState<ArchiveView>('active');
  React.useEffect(() => {
    const onSync = (e: Event) => setValue((e as CustomEvent).detail);
    window.addEventListener(`${eventPrefix}-sync`, onSync);
    return () => window.removeEventListener(`${eventPrefix}-sync`, onSync);
  }, [eventPrefix]);
  const setAndDispatch = (v: ArchiveView) => {
    setValue(v);
    window.dispatchEvent(new CustomEvent(`${eventPrefix}-set`, { detail: v }));
  };
  return [value, setAndDispatch];
}

const Header: React.FC<HeaderProps> = ({
  activeView,
  onViewChange,
  onSuggestVendor,
  timelineView = 'gantt',
  onTimelineViewChange,
  onAddEvent,
  onAddVehicle,
  driverId,
  driverName,
  vehicleId,
  vehicleName,
  currentTaskId,
  taskName,
  decorElementCategory,
  decorElementId,
  decorElementName,
  vendorId,
  vendorName,
  vendorProfileId,
  vendorProfileName,
  vendorRatingId,
  vendorRatingName,
  stationeryItemId,
  stationeryItemName,
  stationeryItemArchived = false,
  taskArchived = false,
  catalogueItemId,
  catalogueItemName,
  menuItemName,
  menuItemId,
  guidelineId,
  guidelineName,
  budgetCategoryId,
  budgetCategoryName,
  onContactVendor,
  onRateVendorAction,
  onSaveVendor,
  onDeleteVendor,
  quoteDetailId,
  onFormSave,
  onFormDelete,
  onAddTask,
  activityView,
  onActivityViewChange,
  activityFilter,
  onActivityFilterChange,
  myTasksOnly,
  onMyTasksToggle,
  priorityFilters,
  onPriorityFiltersChange,
  assigneeFilters,
  onAssigneeFiltersChange,
  taskStatusFilter,
  onTaskStatusFilterChange,
  taskWorkflowStatusFilter,
  onTaskWorkflowStatusFilterChange,
  onAddItem,
  onAddSong,
  paymentFilterStatus = 'all',
  onPaymentFilterStatusChange,
  paymentSortField = 'balance',
  onPaymentSortFieldChange,
  paymentsTab = 'payments',
  onPaymentsTabChange,
  stationeryCategory = 'All',
  onStationeryCategoryChange,
  stationeryArchived = false,
  onStationeryArchivedChange,
  stationeryView = 'list',
  onStationeryViewChange,
  musicTab,
  onMusicTabChange,
  onAddGuideline,
  menuTab,
  onMenuTabChange,
  menuView,
  onMenuViewChange,
  eventsTab = 'All',
  onEventsTabChange,
  vendorView = 'table',
  onVendorViewChange,
  vendorStatusFilter = 'active',
  onVendorStatusFilterChange,
  venueStatusFilter = 'active',
  onVenueStatusFilterChange,
  vendorCategoryFilter = '',
  onVendorCategoryFilterChange,
  budgetEventFilter = '',
  onBudgetEventFilterChange,
  budgetEvents = [],
  paymentsEventFilter = '',
  onPaymentsEventFilterChange,
  budgetView = 'cards',
  onBudgetViewChange,
  onAddBudgetCategory,
  onDeleteBudgetCategory,
  budgetCategoryTab = 'vendors',
  onBudgetCategoryTabChange,
  onAddBudgetVendor,
  onSaveBudgetCategory,
  isEditingBudgetVendor,
  onSaveBudgetVendor,
  onCancelBudgetVendor,
  logisticsTab = 'assignments',
  onLogisticsTabChange,
  onAddDriver,
  onAddAssignment,
  logisticsAssignmentFormOpen,
  logisticsAssignmentIsEditing,
  assignmentFormStatus = 'pending',
  onAssignmentFormStatusChange,
  onCancelAssignment,
  rosEventFilter = 'ALL',
  onRosEventFilterChange,
  rosEventTitles = [],
  onAutomationToggle,
  onVendorRefresh,
  calendarViewMode = 'week',
  onCalendarViewModeChange,
  calendarLabel = '',
  onCalendarPrev,
  onCalendarNext,
  onCalendarToday,
  onGlobalRefresh,
  isSyncing = false,
  onSaveSettings,
  onResetSettings,
  settingsHasChanges = false,
  settingsSaving = false,
  eventList,
  onEventNavigate,
  vendorList,
  onVendorNavigate,
  edTab,
  onEdTabChange,
  edEventId,
  onEdEventChange,
  edEvents,
  edWaiterMode,
  onEdWaiterModeChange,
  onAddDecorElement,
  decorCategoryFilter = 'ALL',
  onDecorCategoryFilterChange,
  decorStatusFilter = 'ALL',
  onDecorStatusFilterChange,
  decorEventFilter = '',
  onDecorEventFilterChange,
  decorEvents = [],
  onMobileMenuToggle,
  edKbViewMode = 'kanban',
  onEdKbViewModeChange,
  edPhase = 0,
  onEdPhaseChange,
  edActiveTab,
}): JSX.Element => {
  const { canEdit } = useRole();
  const canEditBudget = canEdit;
  const { showSuccess, showError, showInfo, unreadCount } = useNotifications();
  // Subscribes the header to project switches (the value itself is not needed here).
  useProject();
  // Re-render when the record numbers load, so crumbs read PO-130 rather than a uuid.
  useRecordRefs();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditingPayment = activeView === 'payment-record' && !location.pathname.endsWith('/new');
  const isPaymentLocked = isEditingPayment && (location.state as { editPayment?: { status?: string } } | null)?.editPayment?.status === 'completed';
  const _paymentUrlId = useMemo(
    () => parseIdFromPath('PY', location.pathname, '/payments/record/'),
    [location.pathname],
  );
  const editPaymentId = isEditingPayment ? ((location.state as any)?.editPayment?.id ?? _paymentUrlId) : null;
  const contributorState = (location.state as any)?.contributor as { id?: string; name?: string } | undefined;
  const isEditingContributor = activeView === 'contributor-form' && !location.pathname.endsWith('/new');
  const _contributorUrlId = useMemo(
    () => parseIdFromPath('CN', location.pathname, '/payments/contributor/'),
    [location.pathname],
  );
  const contributorId = isEditingContributor ? (contributorState?.id ?? _contributorUrlId) : null;
  const money = useMoney();
  const eventsBar = useEventsBar();
  const procurementBar = useProcurementBar();
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  // Below 480px there isn't room for the full breadcrumb trail plus the
  // filter/view controls that share the same row — collapse to "… / Last"
  // regardless of crumb count instead of only truncating past 3 crumbs.
  const [isNarrowBreadcrumb, setIsNarrowBreadcrumb] = useState(() => window.innerWidth < 480);
  useEffect(() => {
    const onResize = () => setIsNarrowBreadcrumb(window.innerWidth < 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [bundleFormOpen, setBundleFormOpen] = useState(false);
  // Bundles grid/table toggle — lives up here in the breadcrumb action row
  // (like every other view switcher) rather than floating in the page body.
  const [bundleViewMode, setBundleViewMode] = useState<'grid' | 'table'>('grid');
  const [bundleArchivedFilter, setBundleArchivedFilter] = useState<ArchiveView>('active');
  const [vehicleViewMode, setVehicleViewMode] = useState<'grid' | 'table'>('grid');
  const [driverViewMode, setDriverViewMode] = useState<'grid' | 'table'>('grid');
  const [quotesViewMode, setQuotesViewMode] = useState<'grid' | 'table'>('table');
  // Schedule (Logistics assignments) Timeline/Table toggle — same
  // header-toolbar-tabs convention as the Bundles grid/table toggle above.
  // LogisticsManager already had the table-view render branch built and a
  // Timeline/Table toggle drawn in a page-body getCustomElement() that was
  // never actually rendered anywhere — this is that toggle, wired up here.
  const [assignmentViewMode, setAssignmentViewMode] = useState<'timeline' | 'table'>('timeline');
  const [bundleIsEdit, setBundleIsEdit] = useState(false);
  // Where an open bundle is in its archive -> trash -> delete lifecycle.
  const [bundleStage, setBundleStage] = useState<'active' | 'archived' | 'trashed'>('active');
  const [bundleEditName, setBundleEditName] = useState<string | null>(null);
  const [fpRoomType, setFpRoomType] = useState('other');
  const [fpIsAdding, setFpIsAdding] = useState(false);
  const [fpActiveName, setFpActiveName] = useState<string | null>(null);
  const [fpVenueName, setFpVenueName] = useState<string | null>(null);
  const [fpZoomLevel, setFpZoomLevel] = useState(100);
  const [quoteCategories, setQuoteCategories] = useState<{ id: string; name: string }[]>([]);
  const [quoteStatusFilter, setQuoteStatusFilter] = useState('all');
  const [quoteCategoryFilter, setQuoteCategoryFilter] = useState('');
  const [quoteDetailStatus, setQuoteDetailStatus] = useState('pending');
  const [quoteDetailDirty, setQuoteDetailDirty] = useState(false);
  const [quoteNewOpen, setQuoteNewOpen] = useState(false);
  useEffect(() => {
    const handler = (e: Event) => setQuoteCategories((e as CustomEvent<{ id: string; name: string }[]>).detail);
    window.addEventListener('quote-categories-ready', handler);
    return () => window.removeEventListener('quote-categories-ready', handler);
  }, []);
  useEffect(() => {
    const handler = (e: Event) => setQuoteDetailStatus((e as CustomEvent<string>).detail);
    window.addEventListener('quote:detail-status', handler);
    return () => window.removeEventListener('quote:detail-status', handler);
  }, []);
  useEffect(() => {
    const handler = (e: Event) => setQuoteDetailDirty((e as CustomEvent<boolean>).detail);
    window.addEventListener('quote:detail-dirty', handler);
    return () => window.removeEventListener('quote:detail-dirty', handler);
  }, []);
  useEffect(() => {
    const handler = (e: Event) => setQuoteNewOpen((e as CustomEvent<boolean>).detail);
    window.addEventListener('quote:new-status', handler);
    return () => window.removeEventListener('quote:new-status', handler);
  }, []);
  const [decorStatus, setDecorStatus] = useState<string>('planned');
  const [catalogCustomizable, setCatalogCustomizable] = useState(false);
  const [catalogArchivedFilter, setCatalogArchivedFilter] = useState<ArchiveView>('active');
  // 3-state (active/archived/all) per tab, matching Venues — was a plain
  // Show/Hide toggle that couldn't express "everything regardless of status".
  const [logisticsArchiveFilterByTab, setLogisticsArchiveFilterByTab] = useState<Record<string, ArchiveView>>({});
  // Decor's own Show Archived — same reasoning as Logistics above: was a
  // button floating in the page body below the real breadcrumb instead of
  // living in it, so it never lined up with the category/status/event
  // filters that already sit in this bar for the same page.
  const [decorArchiveFilter, setDecorArchiveFilter] = useArchiveFilterSync('decor-archived');
  const [menuArchiveFilter, setMenuArchiveFilter] = useArchiveFilterSync('menu-archived');
  const [musicArchiveFilter, setMusicArchiveFilter] = useArchiveFilterSync('music-archived');
  const [contributorArchiveFilter, setContributorArchiveFilter] = useArchiveFilterSync('contributor-archived');
  const [edConnected, setEdConnected] = useState<boolean | null>(null);
  // Menu categories of the Waiter screen, reported by WaiterView (see waiterMenuTabs.ts).
  const [waiterMenu, setWaiterMenu] = useState<WaiterMenuTabsState | null>(null);
  useEffect(() => {
    const handler = (e: Event) => setWaiterMenu((e as CustomEvent<WaiterMenuTabsState | null>).detail);
    window.addEventListener(WAITER_MENU_TABS_EVENT, handler);
    return () => window.removeEventListener(WAITER_MENU_TABS_EVENT, handler);
  }, []);
  const [edClock, setEdClock] = useState(() => new Date());
  const [systemStatusOpen, setSystemStatusOpen] = useState(false);
  const systemStatusRef = useRef<HTMLDivElement>(null);
  // Event Day Hub fullscreen — hides this component's own top header bar
  // (the one with GlobalSearch) and tells EventDayDashboard (which has no
  // direct prop access to this component) to expand into the freed space,
  // via the same CustomEvent bridge pattern used by venue-status-sync etc.
  const [edFullscreen, setEdFullscreen] = useState(false);
  const [shippingAgentPanelLabel, setShippingAgentPanelLabel] = useState<string | null>(null);
  const [shippingAgentActive, setShippingAgentActive] = useState(true);
  const [shippingAgentIsNew, setShippingAgentIsNew] = useState(false);
  const [taskStatus, setTaskStatus] = useState<string>('Not Started');
  // blockedOn is a separate facet from taskStatus above (2026-09-19) — a task
  // can be "In Progress" and blocked at once, so it gets its own toggle next
  // to the status stepper instead of being a 4th step on it. '' = not blocked.
  const [taskBlockedOn, setTaskBlockedOn] = useState<string>('');
  const [vehicleStatus, setVehicleStatus] = useState<string>('available');
  const [vehicleArchived, setVehicleArchived] = useState(false);
  const [driverArchived, setDriverArchived] = useState(false);
  const [venueArchived, setVenueArchived] = useState(false);
  const [vendorCategories, setVendorCategories] = useState<string[]>(() => loadVendorCategories());
  const [isEventDay, setIsEventDay] = useState(false);

  useEffect(() => {
    void refreshVendorCategoriesFromApi().then(setVendorCategories);
  }, []);

  useEffect(() => {
    if (activeView !== 'event-day') return;
    const id = setInterval(() => setEdClock(new Date()), 1000);
    return () => clearInterval(id);
  }, [activeView]);

  // Leaving the Event Day overview tab must not leave the top header
  // permanently hidden — exit fullscreen and tell EventDayDashboard too.
  useEffect(() => {
    if (activeView === 'event-day' && edTab === 'overview') return;
    if (!edFullscreen) return;
    setEdFullscreen(false);
    window.dispatchEvent(new CustomEvent('event-day-fullscreen', { detail: false }));
  }, [activeView, edTab, edFullscreen]);

  useEffect(() => {
    if (!systemStatusOpen) return;
    const onClick = (e: MouseEvent) => {
      if (systemStatusRef.current && !systemStatusRef.current.contains(e.target as Node)) {
        setSystemStatusOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [systemStatusOpen]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    try {
      // Load event dates from DB, not hardcoded
      const events = loadEvents();
      const eventDays = events.map(e => e.date?.slice(0, 10)).filter(Boolean);
      setIsEventDay(eventDays.includes(today));
    } catch {
      // Graceful fallback if events unavailable
      setIsEventDay(false);
    }
  }, []);

  useEffect(() => {
    const handler = (e: Event) => setDecorStatus((e as CustomEvent<string>).detail);
    window.addEventListener('decor-element-status-changed', handler);
    return () => window.removeEventListener('decor-element-status-changed', handler);
  }, []);

  useEffect(() => {
    const onSync = (e: Event) => setVenueArchived((e as CustomEvent<boolean>).detail);
    window.addEventListener('venue-status-sync', onSync);
    return () => window.removeEventListener('venue-status-sync', onSync);
  }, []);



  useEffect(() => {
    const onSync = (e: Event) => setCatalogCustomizable((e as CustomEvent).detail as boolean);
    window.addEventListener('catalog-customizable-sync', onSync);
    return () => window.removeEventListener('catalog-customizable-sync', onSync);
  }, []);

  useEffect(() => {
    const onSync = (e: Event) => setCatalogArchivedFilter((e as CustomEvent).detail as ArchiveView);
    window.addEventListener('catalog-archived-sync', onSync);
    return () => window.removeEventListener('catalog-archived-sync', onSync);
  }, []);

  useEffect(() => {
    const onSync = (e: Event) => setBundleArchivedFilter((e as CustomEvent).detail as ArchiveView);
    window.addEventListener('bundle-archived-sync', onSync);
    return () => window.removeEventListener('bundle-archived-sync', onSync);
  }, []);

  // Logistics (Vehicles/Drivers/Assignments) "Show Archived" — was a button
  // inline in the page body on each tab instead of the breadcrumb actions
  // row every other list page uses. LogisticsManager owns the real
  // per-tab state; this just mirrors it so the button can render here.
  useEffect(() => {
    const onSync = (e: Event) => {
      const { tab, value } = (e as CustomEvent).detail as { tab: string; value: ArchiveView };
      setLogisticsArchiveFilterByTab(prev => ({ ...prev, [tab]: value }));
    };
    window.addEventListener('logistics-archived-sync', onSync);
    return () => window.removeEventListener('logistics-archived-sync', onSync);
  }, []);



  // Sync isAddingRoom state from FloorPlanEditor (e.g. after placing a room)
  useEffect(() => {
    const handler = (e: Event) => setFpIsAdding((e as CustomEvent).detail);
    window.addEventListener('floor-plan-adding-changed', handler);
    return () => window.removeEventListener('floor-plan-adding-changed', handler);
  }, []);

  // Sync active venue name + floor plan name from VenueManager
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ venueName: string | null; fpName: string | null; zoneType: string }>).detail;
      setFpVenueName(detail.venueName);
      setFpActiveName(detail.fpName);
    };
    window.addEventListener('venue-fp-state', handler);
    return () => window.removeEventListener('venue-fp-state', handler);
  }, []);

  // Sync zoom level from FloorPlanEditor (GAP 1)
  useEffect(() => {
    const handler = (e: Event) => {
      const zoom = (e as CustomEvent<number>).detail;
      setFpZoomLevel(Math.round(zoom * 100));
    };
    window.addEventListener('floor-plan-zoom-changed', handler);
    return () => window.removeEventListener('floor-plan-zoom-changed', handler);
  }, []);

  // Track EventDay live connection status (broadcast by EventDayDashboard)
  useEffect(() => {
    const handler = (e: Event) => setEdConnected((e as CustomEvent<{ connected: boolean }>).detail.connected);
    window.addEventListener('event-day-status', handler);
    return () => window.removeEventListener('event-day-status', handler);
  }, []);

  // Sync shipping agent label + active/new status from ShippingAgentEdit
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ label: string; active?: boolean; isNew?: boolean }>).detail;
      setShippingAgentPanelLabel(detail.label);
      setShippingAgentActive(detail.active ?? true);
      setShippingAgentIsNew(!!detail.isNew);
    };
    window.addEventListener('shipping-agent-loaded', handler);
    return () => window.removeEventListener('shipping-agent-loaded', handler);
  }, []);


  // Sync task status from TaskFormPage
  useEffect(() => {
    const handler = (e: Event) => setTaskStatus((e as CustomEvent<string>).detail);
    window.addEventListener('task-form-status-loaded', handler);
    return () => window.removeEventListener('task-form-status-loaded', handler);
  }, []);

  useEffect(() => {
    const handler = (e: Event) => setTaskBlockedOn((e as CustomEvent<string>).detail ?? '');
    window.addEventListener('task-form-blockedOn-loaded', handler);
    return () => window.removeEventListener('task-form-blockedOn-loaded', handler);
  }, []);

  // Sync vehicle status from VehicleFormPage / LogisticsManager
  useEffect(() => {
    const handler = (e: Event) => setVehicleStatus((e as CustomEvent<string>).detail);
    window.addEventListener('vehicle-status-init', handler);
    return () => window.removeEventListener('vehicle-status-init', handler);
  }, []);

  // Sync vehicle archived state from LogisticsManager (route-based vehicle
  // edit page) — same event-bridge convention as vehicle-status-init, since
  // Header and the routed vehicle page aren't in the same React tree.
  useEffect(() => {
    const handler = (e: Event) => setVehicleArchived((e as CustomEvent<boolean>).detail);
    window.addEventListener('vehicle-archived-init', handler);
    return () => window.removeEventListener('vehicle-archived-init', handler);
  }, []);

  // Same for driver — see 'driver-detail' case below.
  useEffect(() => {
    const handler = (e: Event) => setDriverArchived((e as CustomEvent<boolean>).detail);
    window.addEventListener('driver-archived-init', handler);
    return () => window.removeEventListener('driver-archived-init', handler);
  }, []);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      setBundleFormOpen(true);
      setBundleIsEdit(!!detail.isEdit);
      setBundleEditName(detail.name ?? null);
      setBundleStage(detail.trashed ? 'trashed' : detail.archived ? 'archived' : 'active');
    };
    const onClose = () => { setBundleFormOpen(false); setBundleIsEdit(false); setBundleEditName(null); setBundleStage('active'); };
    window.addEventListener('bundle:form-open', onOpen);
    window.addEventListener('bundle:form-close', onClose);
    return () => {
      window.removeEventListener('bundle:form-open', onOpen);
      window.removeEventListener('bundle:form-close', onClose);
    };
  }, []);

  // ── Action button handlers ────────────────────────────────────────────────

  const handlePrintPreview = () => {
    window.print();
  };

  const handleEdit = () => {
    // Edit mode is view-specific; the active view's component handles inline editing
  };

  const handleGlobalSync = async () => {
    try {
      showInfo('Syncing all data...');
      await onGlobalRefresh?.();
      // Pages that registered their own refresh (Team, Communications) reload too.
      await runPageRefresh();
      showSuccess('Sync complete.');
    } catch (error) {
      showError('Failed to sync data');
    }
  };

  const handleAddVendor = () => {
    onSuggestVendor();
  };

  // ── Contextual action buttons ─────────────────────────────────────────────
  // 3-tier hierarchy: Primary (1 filled) | Secondary (outlined) | Overflow (⋯ menu)

  const getActionButtons = () => {
    const overflowMenu = (items: ActionMenuItem[]) => (
      <ActionMenu items={items} position="bottom-right" />
    );

    switch (activeView) {

      case 'communications':
        return <CommunicationsBar />;

      case 'guests':
        // the list carries the bar; a guest's own pages (new, profile, edit) have their buttons in the page
        return matchGuestsRoute(location.pathname)?.kind === 'list' ? <GuestsListBar /> : null;

      case 'team':
        return <TeamBar />;

      case 'team-member-form':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={() => window.dispatchEvent(new CustomEvent('team-member-form-save'))} title="Save">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save</span>
            </button>
            <button className="header-action-btn" onClick={() => window.dispatchEvent(new CustomEvent('team-member-form-cancel'))}>
              Cancel
            </button>
          </>
        );

      case 'vendor-compare':
        return null;
      case 'vendors':
        return (
          <>
            <div className="header-toolbar-tabs">
              <button
                className={`header-toolbar-tab${vendorView === 'table' ? ' active' : ''}`}
                onClick={() => onVendorViewChange?.('table')}
              >
                <Icon name="list" size={14} />
              </button>
              <button
                className={`header-toolbar-tab${vendorView === 'cards' ? ' active' : ''}`}
                onClick={() => onVendorViewChange?.('cards')}
              >
                <Icon name="grid" size={14} />
              </button>
            </div>
            <div className="header-filter-group">
              <SearchableSelect
                className="header-filter-select"
                value={vendorCategoryFilter}
                onChange={v => onVendorCategoryFilterChange?.(v)}
                options={[
                  { value: '', label: 'All Categories' },
                  ...vendorCategories.map(cat => ({ value: cat, label: cat })),
                ]}
              />
            </div>
            <div className="header-filter-group">
              <ArchivedToggle
                checked={isArchiveView(vendorStatusFilter)}
                onChange={on => onVendorStatusFilterChange?.(toArchiveView(on))}
              />
            </div>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={handleAddVendor} title="New Vendor">
              <Icon name="plus" size={16} />
              <span className="header-action-btn__label">New Vendor</span>
            </button>
            <ExportButton exportKey="vendors" />
            <button className="header-action-btn header-action-btn--iconic" onClick={() => navigate('/vendors/compare')} title="Compare">
              <Icon name="list" size={16} />
              <span className="header-action-btn__label">Compare</span>
            </button>
          </>
        );



      case 'budget': {
        const budgetActions: BreadcrumbAction[] = [
          // Was a separate toggle button that had to be clicked before this
          // dropdown appeared — redundant with the dropdown itself, which
          // already reads "All Events" when nothing's selected. Just show it.
          ...(budgetEvents.length > 0 ? [{
            kind: 'filter' as const,
            key: 'event-filter',
            value: budgetEventFilter ?? '',
            onChange: (v: string) => onBudgetEventFilterChange?.(v),
            options: [{ value: '', label: 'All Events' }, ...budgetEvents.map(ev => ({ value: ev.id, label: ev.title }))],
            visible: true,
          }] : []),
          {
            kind: 'tabs',
            key: 'view-mode',
            value: budgetView ?? 'cards',
            onChange: (v: string) => onBudgetViewChange?.(v as 'cards' | 'table'),
            tabs: [
              { value: 'cards', icon: 'grid', title: 'Cards' },
              { value: 'table', icon: 'list', title: 'Table' },
            ],
          },
          { kind: 'button', key: 'add-category', label: 'Add Category', icon: 'plus', onClick: () => onAddBudgetCategory?.() },
          { kind: 'button', key: 'print', icon: 'document', title: 'Print', onClick: handlePrintPreview },
        ];
        return <BreadcrumbBar actions={budgetActions} />;
      }

      case 'budget-variances':
        return (
          <BreadcrumbBar
            actions={canEditBudget ? [{ kind: 'button', key: 'add-variance', label: 'Add Variance', icon: 'plus', onClick: () => window.dispatchEvent(new CustomEvent('budget-variance:add')) }] : []}
          />
        );

      case 'asoebi':
      case 'asoebi-bundles': {
        // The Bundles tab. Fabrics and Orders carry their own in-page actions.
        if (bundleFormOpen) {
          return (
            <>
              <button className="header-action-btn" onClick={() => window.dispatchEvent(new CustomEvent('bundle:cancel'))}>
                Cancel
              </button>
              <button className="header-action-btn header-action-btn--iconic primary" onClick={() => window.dispatchEvent(new CustomEvent('bundle:save'))} title="Save">
                <Icon name="check" size={16} /> <span className="header-action-btn__label">Save</span>
              </button>
              {bundleIsEdit && (
                <>
                  <div className="header-divider" />
                  {bundleStage === 'active' && (
                    <button className="header-action-btn header-action-btn--iconic" onClick={() => window.dispatchEvent(new CustomEvent('bundle:archive'))} title="Hide this bundle from the active list">
                      <Icon name="trash" size={16} /> <span className="header-action-btn__label">Archive</span>
                    </button>
                  )}
                  {bundleStage === 'archived' && (
                    <>
                      <button className="header-action-btn" onClick={() => window.dispatchEvent(new CustomEvent('bundle:unarchive'))}>
                        Restore
                      </button>
                      <button className="header-action-btn header-action-btn--iconic header-btn-danger" onClick={() => window.dispatchEvent(new CustomEvent('bundle:trash'))} title="Move this archived bundle to trash">
                        <Icon name="trash" size={16} /> <span className="header-action-btn__label">Move to trash</span>
                      </button>
                    </>
                  )}
                  {bundleStage === 'trashed' && (
                    <>
                      <button className="header-action-btn" onClick={() => window.dispatchEvent(new CustomEvent('bundle:restore'))}>
                        Restore
                      </button>
                      <button className="header-action-btn header-action-btn--iconic header-btn-danger" onClick={() => window.dispatchEvent(new CustomEvent('bundle:delete'))} title="Delete this bundle forever">
                        <Icon name="trash" size={16} /> <span className="header-action-btn__label">Delete forever</span>
                      </button>
                    </>
                  )}
                </>
              )}
            </>
          );
        }
        return (
          <>
            <div className="header-toolbar-tabs">
              <button
                className={`header-toolbar-tab${bundleViewMode === 'grid' ? ' active' : ''}`}
                onClick={() => { setBundleViewMode('grid'); window.dispatchEvent(new CustomEvent('bundle:view-mode', { detail: 'grid' })); }}
                title="Card view"
              >
                <Icon name="grid" size={14} />
              </button>
              <button
                className={`header-toolbar-tab${bundleViewMode === 'table' ? ' active' : ''}`}
                onClick={() => { setBundleViewMode('table'); window.dispatchEvent(new CustomEvent('bundle:view-mode', { detail: 'table' })); }}
                title="Table view"
              >
                <Icon name="list" size={14} />
              </button>
            </div>
            <div className="header-filter-group">
              <ArchivedToggle
                checked={isArchiveView(bundleArchivedFilter)}
                onChange={on => {
                  const v = toArchiveView(on);
                  setBundleArchivedFilter(v);
                  window.dispatchEvent(new CustomEvent('bundle-archived-set', { detail: v }));
                }}
              />
            </div>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={() => window.dispatchEvent(new CustomEvent('bundle:new'))} title="New Bundle">
              <Icon name="plus" size={16} />
              <span className="header-action-btn__label">New Bundle</span>
            </button>
          </>
        );
      }

      case 'timeline':
        return (
          <>
            {/* The Gantt now plots programme items nested under each event, so the
                event filter is as useful there as it is on the Run of Show. */}
            {(timelineView === 'run-of-show' || timelineView === 'gantt') && rosEventTitles.length > 0 && (
              <div className="header-filter-group">
                <SearchableSelect
                  className="header-filter-select"
                  value={rosEventFilter}
                  onChange={v => onRosEventFilterChange?.(v)}
                  options={[
                    { value: 'ALL', label: 'All Events' },
                    ...rosEventTitles.map(title => ({ value: title, label: title })),
                  ]}
                />
              </div>
            )}
            <div className="header-toolbar-tabs">
              <button
                className={`header-toolbar-tab ${timelineView === 'gantt' ? 'active' : ''}`}
                onClick={() => onTimelineViewChange?.('gantt')}
                title="Gantt"
              >
                <Icon name="timeline" size={14} />
              </button>
              <button
                className={`header-toolbar-tab ${timelineView === 'sequence' ? 'active' : ''}`}
                onClick={() => onTimelineViewChange?.('sequence')}
                title="Sequence"
              >
                <Icon name="list" size={14} />
              </button>
              <button
                className={`header-toolbar-tab ${timelineView === 'run-of-show' ? 'active' : ''}`}
                onClick={() => onTimelineViewChange?.('run-of-show')}
                title="Run of Show"
              >
                <Icon name="clock" size={14} />
              </button>
              <button
                className="header-toolbar-tab"
                onClick={() => onViewChange('calendar')}
                title="Calendar"
              >
                <Icon name="calendar" size={14} />
              </button>
            </div>
            <button
              className="header-action-btn header-action-btn--iconic primary"
              onClick={onAddEvent || (() => showInfo('Opening add event modal...'))}
             title="New Event">
              <Icon name="plus" size={14} />
              <span className="header-action-btn__label">New Event</span>
            </button>
            <IconButton icon="document" label="Print" onClick={handlePrintPreview} />
          </>
        );

      case 'activities':
        return (
          <>
            <TaskFilters
              myTasksOnly={!!myTasksOnly}
              onMyTasksToggle={() => onMyTasksToggle?.()}
              priorityFilters={priorityFilters ?? []}
              onPriorityFiltersChange={v => onPriorityFiltersChange?.(v)}
              assigneeFilters={assigneeFilters ?? []}
              onAssigneeFiltersChange={v => onAssigneeFiltersChange?.(v)}
              assigneeOptions={loadAssigneeOptions()}
              statusFilter={taskWorkflowStatusFilter ?? DEFAULT_TASK_STATUS_FILTER}
              onStatusFilterChange={v => onTaskWorkflowStatusFilterChange?.(v)}
              archived={isArchiveView(taskStatusFilter)}
              onArchivedChange={on => onTaskStatusFilterChange?.(toArchiveView(on))}
              tabShortcut={activityFilter}
              onTabShortcutClear={() => onActivityFilterChange?.('All')}
            />
            <div className="header-toolbar-tabs">
              <button className={`header-toolbar-tab${(activityView ?? 'list') === 'list' ? ' active' : ''}`} onClick={() => onActivityViewChange?.('list')} title="List">
                <Icon name="list" size={14} />
              </button>
              <button className={`header-toolbar-tab${(activityView ?? 'list') === 'kanban' ? ' active' : ''}`} onClick={() => onActivityViewChange?.('kanban')} title="Kanban">
                <Icon name="grid" size={14} />
              </button>
              <button className={`header-toolbar-tab${(activityView ?? 'list') === 'assignee' ? ' active' : ''}`} onClick={() => onActivityViewChange?.('assignee')} title="By Assignee">
                <Icon name="users" size={14} />
              </button>
            </div>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onAddTask || (() => showInfo('Opening add task modal...'))} title="New Task">
              <Icon name="plus" size={16} />
              <span className="header-action-btn__label">New Task</span>
            </button>
            <ExportButton exportKey="tasks" variant="icon" />
          </>
        );

      case 'menu':
        return (
          <>
            <div className="header-toolbar-tabs">
              <button className={`header-toolbar-tab${(menuView ?? 'card') === 'card' ? ' active' : ''}`} onClick={() => onMenuViewChange?.('card')} title="Cards"><Icon name="grid" size={14} /></button>
              <button className={`header-toolbar-tab${(menuView ?? 'card') === 'list' ? ' active' : ''}`} onClick={() => onMenuViewChange?.('list')} title="List"><Icon name="list" size={14} /></button>
            </div>
            <div className="header-filter-group">
              <ArchivedToggle
                checked={isArchiveView(menuArchiveFilter)}
                onChange={on => setMenuArchiveFilter(toArchiveView(on))}
              />
            </div>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={() => onViewChange('menu-item-new')} title="New Item">
              <Icon name="plus" size={16} />
              <span className="header-action-btn__label">New Item</span>
            </button>
            <IconButton icon="document" label="Print" onClick={handlePrintPreview} />
          </>
        );

      case 'logistics':
        return (
          <>
            {logisticsTab === 'drivers' && (
              <>
                <div className="header-toolbar-tabs">
                  <button
                    className={`header-toolbar-tab${driverViewMode === 'grid' ? ' active' : ''}`}
                    onClick={() => { setDriverViewMode('grid'); window.dispatchEvent(new CustomEvent('driver-view-mode', { detail: 'grid' })); }}
                    title="Card view"
                  >
                    <Icon name="grid" size={14} />
                  </button>
                  <button
                    className={`header-toolbar-tab${driverViewMode === 'table' ? ' active' : ''}`}
                    onClick={() => { setDriverViewMode('table'); window.dispatchEvent(new CustomEvent('driver-view-mode', { detail: 'table' })); }}
                    title="Table view"
                  >
                    <Icon name="list" size={14} />
                  </button>
                </div>
                <button className="header-action-btn header-action-btn--iconic primary" onClick={onAddDriver} title="New Driver"><Icon name="plus" size={16} /> <span className="header-action-btn__label">New Driver</span></button>
              </>
            )}
            {logisticsTab === 'vehicles' && (
              <>
                <div className="header-toolbar-tabs">
                  <button
                    className={`header-toolbar-tab${vehicleViewMode === 'grid' ? ' active' : ''}`}
                    onClick={() => { setVehicleViewMode('grid'); window.dispatchEvent(new CustomEvent('vehicle-view-mode', { detail: 'grid' })); }}
                    title="Card view"
                  >
                    <Icon name="grid" size={14} />
                  </button>
                  <button
                    className={`header-toolbar-tab${vehicleViewMode === 'table' ? ' active' : ''}`}
                    onClick={() => { setVehicleViewMode('table'); window.dispatchEvent(new CustomEvent('vehicle-view-mode', { detail: 'table' })); }}
                    title="Table view"
                  >
                    <Icon name="list" size={14} />
                  </button>
                </div>
                <button className="header-action-btn header-action-btn--iconic primary" onClick={onAddVehicle} title="New Vehicle"><Icon name="plus" size={16} /> <span className="header-action-btn__label">New Vehicle</span></button>
              </>
            )}
            {logisticsTab === 'assignments' && !logisticsAssignmentFormOpen && (
              <>
                <div className="header-toolbar-tabs">
                  <button
                    className={`header-toolbar-tab${assignmentViewMode === 'timeline' ? ' active' : ''}`}
                    onClick={() => { setAssignmentViewMode('timeline'); window.dispatchEvent(new CustomEvent('logistics-assignment-view-mode', { detail: 'timeline' })); }}
                    title="Timeline view"
                  >
                    <Icon name="clock" size={14} />
                  </button>
                  <button
                    className={`header-toolbar-tab${assignmentViewMode === 'table' ? ' active' : ''}`}
                    onClick={() => { setAssignmentViewMode('table'); window.dispatchEvent(new CustomEvent('logistics-assignment-view-mode', { detail: 'table' })); }}
                    title="Table view"
                  >
                    <Icon name="list" size={14} />
                  </button>
                </div>
                <button className="header-action-btn header-action-btn--iconic primary" onClick={onAddAssignment} title="New Movement"><Icon name="plus" size={16} /> <span className="header-action-btn__label">New Movement</span></button>
              </>
            )}
            {logisticsTab === 'assignments' && logisticsAssignmentFormOpen && (<>
              {/* Status as a workflow tracker rather than a plain <select>.
                  Placed in the actions row, not the centered breadcrumb slot,
                  because that slot is hidden below 900px and status still
                  needs to be settable on narrow viewports. Same
                  .header-status-steps markup as procurement and quotes. */}
              <div className="header-status-stepper-wrap">
                {(() => {
                  const cancelled = assignmentFormStatus === 'cancelled';
                  const MOVEMENT_STEPS: Array<{ value: string; label: string; tone: HeaderStep['tone'] }> = [
                    { value: 'pending',   label: 'Pending',   tone: 'idle' },
                    { value: 'confirmed', label: 'Confirmed', tone: 'active' },
                    { value: 'completed', label: cancelled ? 'Cancelled' : 'Completed', tone: cancelled ? 'failed' : 'done' },
                  ];
                  const idx = MOVEMENT_STEPS.findIndex(x => x.value === assignmentFormStatus);
                  const cur = cancelled ? MOVEMENT_STEPS.length - 1 : idx;
                  const steps: HeaderStep[] = MOVEMENT_STEPS.map((x, i) => ({
                    key: x.value,
                    label: x.label,
                    tone: x.tone,
                    disabled: i < cur,
                    onSelect: i < cur ? undefined : () => onAssignmentFormStatusChange?.(x.value),
                  }));
                  return <HeaderStatusSteps steps={steps} currentIndex={cur} ariaLabel="Movement status" />;
                })()}
                {/* No terminal 'cancelled' button: a movement that won't happen
                    is archived instead (the Archive button below, wired to
                    archiveAssignment in LogisticsManager.tsx) — this tracker
                    only needs to carry the normal Pending → Confirmed →
                    Completed progression. The 'Cancelled' label above still
                    renders correctly for any older record that already has
                    that status stored. */}
              </div>
              <span className="header-btn-divider" aria-hidden="true" />
              {logisticsAssignmentIsEditing && (
                <button className="header-action-btn header-action-btn--iconic" title="Archive this movement" onClick={() => window.dispatchEvent(new CustomEvent('logistics-assignment-archive'))}>
                  <Icon name="folder" size={16} />
                  <span className="header-action-btn__label">Archive</span>
                </button>
              )}
              <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save"><Icon name="check" size={16} /> <span className="header-action-btn__label">Save</span></button>
              <button className="header-action-btn" onClick={() => { window.dispatchEvent(new CustomEvent('form-cancel')); onCancelAssignment?.(); }}>Cancel</button>
            </>)}
            {!(logisticsTab === 'assignments' && logisticsAssignmentFormOpen) && (
              <div className="header-filter-group">
                <ArchivedToggle
                  checked={isArchiveView(logisticsArchiveFilterByTab[logisticsTab ?? 'vehicles'])}
                  onChange={on => {
                    const tab = logisticsTab ?? 'vehicles';
                    const value = toArchiveView(on);
                    setLogisticsArchiveFilterByTab(prev => ({ ...prev, [tab]: value }));
                    window.dispatchEvent(new CustomEvent('logistics-archived-set', { detail: { tab, value } }));
                  }}
                />
              </div>
            )}
            <ExportButton exportKey={logisticsTab === 'drivers' ? 'logistics-drivers' : logisticsTab === 'assignments' ? 'logistics-assignments' : 'logistics-vehicles'} />
          </>
        );


      case 'procurement':
        // Procurement orders/shipments/shipping-agents/inventory are now all
        // built (ticket/proc-kal-fe) — the Inventory tracker reads the API-
        // backed inventory_items service via useProcurementBar, same pattern
        // as the other Procurement bars below.
        return <InventoryBar />;
      case 'procurement-orders':
        return <OrdersListBar />;
      case 'procurement-order-new':
        return <OrderDetailBar isNew />;
      case 'procurement-order-edit':
        return <OrderDetailBar />;
      case 'procurement-shipments':
        return <ShipmentsListBar />;
      case 'procurement-shipment-new':
        return <ShipmentDetailBar isNew />;
      case 'procurement-shipment-edit':
        return <ShipmentDetailBar />;
      // The aso-ebi Orders list's filters and New order. The same view id also covers an order's own page,
      // which has its own form and gets no list actions.
      case 'asoebi-orders':
        return isAsoebiOrdersListUrl(location.pathname) ? <AsoebiOrdersBar /> : null;
      case 'asoebi-assignments':
        return location.pathname.replace(/\/+$/, '') === '/asoebi/assignments' ? <AssignmentsListBar /> : <AssignmentEditorBar />;
      case 'gift-registry':
        return <GiftRegistryListBar />;
      case 'gift-registry-new':
      case 'gift-registry-item':
        return <GiftItemBar />;
      case 'vendor-bookings':
        return <VendorBookingsListBar />;
      case 'vendor-booking-edit':
        return <VendorBookingEditBar />;

      case 'shipping-agent-edit':
        return <AgentDetailBar isNew={shippingAgentIsNew} active={shippingAgentActive} />;
      case 'vendor-detail':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onSaveVendor} title="Save Changes">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save Changes</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('vendors')}>
              Cancel
            </button>
            <div className="header-divider" />
            {/* Archives, not a hard delete — VendorTable's vendor-delete
                listener opens an "Archive this vendor?" confirm and does
                PUT { archived: true }. Permanent delete only happens from
                Trash, and only once a record is already archived. Labeled
                and styled to match, same as guest-detail's Archive button. */}
            <button className="header-action-btn header-action-btn--iconic" onClick={onDeleteVendor} title="Archive this vendor">
              <Icon name="folder" size={16} />
              <span className="header-action-btn__label">Archive</span>
            </button>
          </>
        );

      case 'budget-category':
        return (
          <>
            {budgetCategoryTab === 'overview' && (
              <>
                <button className="header-action-btn header-action-btn--iconic primary" onClick={onSaveBudgetCategory} title="Save Changes">
                  <Icon name="check" size={16} />
                  <span className="header-action-btn__label">Save Changes</span>
                </button>
                <button className="header-action-btn" onClick={() => onViewChange('budget')}>
                  Cancel
                </button>
              </>
            )}
            {budgetCategoryTab === 'vendors' && !isEditingBudgetVendor && (
              <>
                <button className="header-action-btn header-action-btn--iconic" onClick={onAddBudgetVendor} title="New Quote">
                  <Icon name="plus" size={16} />
                  <span className="header-action-btn__label">New Quote</span>
                </button>
              </>
            )}
            {budgetCategoryTab === 'vendors' && isEditingBudgetVendor && (
              <>
                <button className="header-action-btn header-action-btn--iconic primary" onClick={onSaveBudgetVendor} title="Save">
                  <Icon name="check" size={16} />
                  <span className="header-action-btn__label">Save</span>
                </button>
                <button className="header-action-btn" onClick={onCancelBudgetVendor}>
                  Cancel
                </button>
              </>
            )}
            {budgetCategoryTab === 'payments' && (
              <button className="header-action-btn header-action-btn--iconic" onClick={() => window.dispatchEvent(new CustomEvent('budget-add-payment'))} title="New Payment">
                <Icon name="plus" size={16} />
                <span className="header-action-btn__label">New Payment</span>
              </button>
            )}
            <ExportButton exportKey={budgetCategoryTab === 'vendors' ? 'budget-category-vendors' : budgetCategoryTab === 'payments' ? 'budget-category-payments' : 'budget-category-summary'} />
            <div className="header-divider" />
            <button className="header-action-btn header-action-btn--iconic action-danger header-btn-danger" onClick={onDeleteBudgetCategory} title="Delete">
              <Icon name="trash" size={16} />
              <span className="header-action-btn__label">Delete</span>
            </button>
          </>
        );




      case 'stationery':
        return (
          <>
            <div className="header-filter-group">
              <SearchableSelect
                className="header-filter-select"
                value={stationeryCategory}
                onChange={v => onStationeryCategoryChange?.(v as any)}
                options={[
                  { value: 'All', label: 'All Categories' },
                  { value: 'invitation', label: 'Invitation' },
                  { value: 'programme', label: 'Programme' },
                  { value: 'signage', label: 'Signage' },
                  { value: 'favour', label: 'Favour' },
                  { value: 'packaging', label: 'Packaging' },
                  { value: 'digital', label: 'Digital' },
                ]}
              />
            </div>
            <div className="header-filter-group">
              <ArchivedToggle checked={stationeryArchived} onChange={on => onStationeryArchivedChange?.(on)} />
            </div>
            <div className="header-toolbar-tabs">
              <button
                className={`header-toolbar-tab ${stationeryView === 'list' ? 'active' : ''}`}
                onClick={() => onStationeryViewChange?.('list')}
              >
                <Icon name="list" size={14} />
              </button>
              <button
                className={`header-toolbar-tab ${stationeryView === 'kanban' ? 'active' : ''}`}
                onClick={() => onStationeryViewChange?.('kanban')}
              >
                <Icon name="grid" size={14} />
              </button>
            </div>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onAddItem || (() => showInfo('Opening add item modal...'))} title="New Item">
              <Icon name="plus" size={16} />
              <span className="header-action-btn__label">New Item</span>
            </button>
            <ExportButton exportKey="stationery" />
          </>
        );

      case 'decor-new':
      case 'decor-edit': {
        const decorSteps = [
          { key: 'planned', label: 'Planned' },
          { key: 'confirmed', label: 'Confirmed' },
          { key: 'procured', label: 'Procured' },
          { key: 'installed', label: 'Installed' },
        ];
        const decorStepIdx = decorSteps.findIndex(s => s.key === decorStatus);
        return (
          <>
            <HeaderStatusSteps
              ariaLabel="Decor status"
              currentIndex={decorStepIdx}
              steps={decorSteps.map<HeaderStep>(x => ({
                key: x.key,
                label: x.label,
                tone: 'active',
                numbered: true,
                onSelect: () => window.dispatchEvent(new CustomEvent('decor-element-set-status', { detail: x.key })),
              }))}
            />
            <span className="header-btn-divider" aria-hidden="true" />
            <button
              className="header-action-btn header-action-btn--iconic primary"
              onClick={() => window.dispatchEvent(new CustomEvent('decor-element-save'))}
            >
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">{activeView === 'decor-new' ? 'Save Element' : 'Save Changes'}</span>
            </button>
            <button
              className="header-action-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('decor-element-cancel'))}
            >
              Cancel
            </button>
            {activeView === 'decor-edit' && decorElementId && (
              <>
                <div className="header-divider" />
                <button
                  className="header-action-btn header-action-btn--iconic header-btn-danger"
                  onClick={() => window.dispatchEvent(new CustomEvent('decor-element-delete'))}
                  title="Archive this item"
                >
                  <Icon name="trash" size={16} />
                  <span className="header-action-btn__label">Archive</span>
                </button>
              </>
            )}
          </>
        );
      }

      case 'decor':
        return (
          <>
            <DecorFilters
              category={decorCategoryFilter} onCategoryChange={v => onDecorCategoryFilterChange?.(v)}
              status={decorStatusFilter} onStatusChange={v => onDecorStatusFilterChange?.(v)}
              event={decorEventFilter} onEventChange={v => onDecorEventFilterChange?.(v)}
              events={decorEvents}
            />
            <div className="header-filter-group">
              <ArchivedToggle
                checked={isArchiveView(decorArchiveFilter)}
                onChange={on => setDecorArchiveFilter(toArchiveView(on))}
              />
            </div>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onAddDecorElement} title="New Element">
              <Icon name="plus" size={16} />
              <span className="header-action-btn__label">New Element</span>
            </button>
          </>
        );

      case 'music':
        return (
          <>
            <div className="header-filter-group">
              <SearchableSelect
                className="header-filter-select"
                value={musicTab ?? 'After Party'}
                onChange={v => onMusicTabChange?.(v)}
                options={[
                  { value: 'After Party', label: 'After Party' },
                  { value: 'Tunnel Music', label: 'Tunnel Music' },
                  { value: 'Church Hymns', label: 'Church Hymns' },
                  { value: 'Guidelines', label: 'Guidelines' },
                ]}
              />
            </div>
            {musicTab !== 'Guidelines' && (
              <div className="header-filter-group">
                <ArchivedToggle
                  checked={isArchiveView(musicArchiveFilter)}
                  onChange={on => setMusicArchiveFilter(toArchiveView(on))}
                />
              </div>
            )}
            <IconButton icon="document" label="DJ Brief" onClick={() => window.dispatchEvent(new CustomEvent('music-dj-brief'))} />
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onAddSong || (() => showInfo('Opening add song modal...'))} title="New Song">
              <Icon name="plus" size={16} />
              <span className="header-action-btn__label">New Song</span>
            </button>
            <ExportButton exportKey="music" />
          </>
        );
      case 'events':
        return (
          <EventsListBar />
        );
      case 'event-new':
        return <EventDetailBar isNew onSave={onFormSave} onCancel={() => onViewChange('events')} />;
      case 'event-detail':
        return <EventDetailBar onSave={onFormSave} onArchive={onFormDelete} onCancel={() => onViewChange('events')} />;
      case 'guidelines':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onAddGuideline} title="New Guideline">
              <Icon name="plus" size={16} />
              <span className="header-action-btn__label">New Guideline</span>
            </button>
            <ExportButton exportKey="guidelines" />
          </>
        );
      case 'guideline-edit':
      case 'stationery-item-edit':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save Changes">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save Changes</span>
            </button>
            <div className="header-divider" />
            {stationeryItemArchived ? (
              <>
                <button
                  className="header-action-btn"
                  onClick={() => window.dispatchEvent(new CustomEvent('stationery-item-restore'))}
                  title="Restore this item"
                >
                  Restore
                </button>
                <button
                  className="header-action-btn header-action-btn--iconic header-btn-danger"
                  onClick={() => window.dispatchEvent(new CustomEvent('stationery-item-permanent-delete'))}
                  title="Permanently delete this item"
                >
                  <Icon name="trash" size={16} />
                  <span className="header-action-btn__label">Delete</span>
                </button>
              </>
            ) : (
              <button className="header-action-btn header-action-btn--iconic header-btn-danger" onClick={onFormDelete} title="Archive this item">
                <Icon name="trash" size={16} />
                <span className="header-action-btn__label">Archive</span>
              </button>
            )}
          </>
        );
      case 'guideline-new':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('guidelines')}>
              Cancel
            </button>
          </>
        );
      case 'stationery-item-new':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('stationery')}>
              Cancel
            </button>
          </>
        );
      case 'vehicle-new':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('logistics')}>
              Cancel
            </button>
          </>
        );
      case 'vehicle-detail': {
        // Vehicle status (available/assigned/in-transit/maintenance) isn't a
        // linear, one-way pipeline the way quote/movement status is — a
        // vehicle cycles available→assigned→in-transit→available again
        // across many bookings, and maintenance can interrupt from any of
        // those rather than sitting at the end of the line. Rendered as the
        // same header-status-steps tracker every other workflow uses anyway
        // (explicit ask, overriding the plain-select design above), but with
        // no forward-only gating — every step stays clickable so a backward
        // or sideways transition (in-transit → available, anything →
        // maintenance) works the same as a forward one.
        const VEHICLE_STATUS_STEPS: Array<{ value: string; label: string; tone: HeaderStep['tone'] }> = [
          { value: 'available', label: 'Available', tone: 'done' },
          { value: 'assigned', label: 'Assigned', tone: 'active' },
          { value: 'in-transit', label: 'In Transit', tone: 'transit' },
          { value: 'maintenance', label: 'Maintenance', tone: 'failed' },
        ];
        return (
          <>
            <div className="header-status-stepper-wrap">
              <HeaderStatusSteps
                ariaLabel="Vehicle status"
                mode="choice"
                currentIndex={VEHICLE_STATUS_STEPS.findIndex(x => x.value === vehicleStatus)}
                steps={VEHICLE_STATUS_STEPS.map<HeaderStep>(x => ({
                  key: x.value,
                  label: x.label,
                  tone: x.tone,
                  onSelect: () => {
                    setVehicleStatus(x.value);
                    window.dispatchEvent(new CustomEvent('vehicle-status-change', { detail: x.value }));
                  },
                }))}
              />
            </div>
            <div className="header-divider" />
            <button className="header-action-btn header-action-btn--iconic primary" onClick={() => window.dispatchEvent(new CustomEvent('vehicle-form-save'))} title="Save Changes">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save Changes</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('logistics')}>
              Cancel
            </button>
            <div className="header-divider" />
            {!vehicleArchived ? (
              <button
                className="header-action-btn header-action-btn--iconic"
                onClick={() => {
                  setVehicleArchived(true);
                  window.dispatchEvent(new CustomEvent('vehicle-archive-toggle', { detail: true }));
                }}
              >
                <Icon name="trash" size={16} />
                <span className="header-action-btn__label">Archive</span>
              </button>
            ) : (
              <>
                <button
                  className="header-action-btn header-action-btn--iconic"
                  onClick={() => {
                    setVehicleArchived(false);
                    window.dispatchEvent(new CustomEvent('vehicle-archive-toggle', { detail: false }));
                  }}
                >
                  <Icon name="refresh" size={16} />
                  <span className="header-action-btn__label">Restore</span>
                </button>
                <button
                  className="header-action-btn header-action-btn--iconic header-btn-danger"
                  onClick={() => window.dispatchEvent(new CustomEvent('vehicle-delete-request'))}
                  title="Permanently delete this vehicle"
                >
                  <Icon name="trash" size={16} />
                  <span className="header-action-btn__label">Delete</span>
                </button>
              </>
            )}
          </>
        );
      }
      case 'driver-detail': {
        // Same event-bridge pattern as vehicle-detail: this route-based page
        // and LogisticsManager (which actually owns driver state) aren't in
        // the same React tree, so Save/Archive/Delete cross via window events
        // rather than props. Used to be a hand-built "Back / Edit Driver /
        // Save Changes" bar inside the page body, with no breadcrumb and no
        // sidebar-matching header actions at all.
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={() => window.dispatchEvent(new CustomEvent('driver-form-save'))} title="Save Changes">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save Changes</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('logistics')}>
              Cancel
            </button>
            <div className="header-divider" />
            {!driverArchived ? (
              <button
                className="header-action-btn header-action-btn--iconic"
                onClick={() => {
                  setDriverArchived(true);
                  window.dispatchEvent(new CustomEvent('driver-archive-toggle', { detail: true }));
                }}
              >
                <Icon name="trash" size={16} />
                <span className="header-action-btn__label">Archive</span>
              </button>
            ) : (
              <>
                <button
                  className="header-action-btn header-action-btn--iconic"
                  onClick={() => {
                    setDriverArchived(false);
                    window.dispatchEvent(new CustomEvent('driver-archive-toggle', { detail: false }));
                  }}
                >
                  <Icon name="refresh" size={16} />
                  <span className="header-action-btn__label">Restore</span>
                </button>
                <button
                  className="header-action-btn header-action-btn--iconic header-btn-danger"
                  onClick={() => window.dispatchEvent(new CustomEvent('driver-delete-request'))}
                  title="Permanently delete this driver"
                >
                  <Icon name="trash" size={16} />
                  <span className="header-action-btn__label">Delete</span>
                </button>
              </>
            )}
          </>
        );
      }
      case 'task-edit':
      case 'task-new': {
        // Matches every other .header-status-steps bar in the app (asoebi
        // orders/assignments, deliveries, shipments, bookings, quotes,
        // logistics) — --workflow-transit (blue) for in-flight work,
        // --workflow-done (green) reserved for settled/complete. This used
        // to give In Progress its own green, which drifted from that
        // semantic convention (green ≠ "the step that's moving").
        // Blocked used to be a 4th step here, but it's a separate blockedOn
        // facet from status now (a task can be "In Progress" AND blocked at
        // once) — so it's its own toggle group beside the stepper instead of
        // a step on it.
        const TASK_STATUS_STEPS = [
          { value: 'Not Started', label: 'Not Started', tone: 'idle' as const },
          { value: 'In Progress', label: 'In Progress', tone: 'transit' as const },
          { value: 'Done',        label: 'Done',        tone: 'done' as const },
        ];
        const TASK_BLOCKED_ON_OPTIONS = [
          { value: '', label: 'Not blocked' },
          { value: 'internal', label: 'Blocked · Internal' },
          { value: 'external', label: 'Blocked · External' },
        ];
        const currentIdx = TASK_STATUS_STEPS.findIndex(s => s.value === taskStatus);
        return (
          <>
            <HeaderStatusSteps
              ariaLabel="Task status"
              currentIndex={currentIdx}
              steps={TASK_STATUS_STEPS.map<HeaderStep>(x => ({
                key: x.value,
                label: x.label,
                tone: x.tone,
                onSelect: () => {
                  setTaskStatus(x.value);
                  window.dispatchEvent(new CustomEvent('task-form-status-change', { detail: x.value }));
                },
              }))}
            />
            {/* Blocked is independent of status: a task can be In Progress and blocked at once. */}
            <HeaderStatusSteps
              ariaLabel="Blocked on"
              mode="choice"
              currentIndex={TASK_BLOCKED_ON_OPTIONS.findIndex(o => o.value === taskBlockedOn)}
              steps={TASK_BLOCKED_ON_OPTIONS.map<HeaderStep>(o => ({
                key: o.value || 'none',
                label: o.label,
                tone: o.value ? 'failed' : 'idle',
                onSelect: () => {
                  setTaskBlockedOn(o.value);
                  window.dispatchEvent(new CustomEvent('task-form-blockedOn-change', { detail: o.value }));
                },
              }))}
            />
            <span className="header-btn-divider" aria-hidden="true" />
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save Task">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save Task</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('activities')}>
              Cancel
            </button>
            {activeView === 'task-edit' && (
              <>
                <div className="header-divider" />
                {taskArchived ? (
                  <button
                    className="header-action-btn"
                    onClick={() => window.dispatchEvent(new CustomEvent('task-form-restore'))}
                    title="Restore this task"
                  >
                    Restore
                  </button>
                ) : (
                  <button
                    className="header-action-btn header-action-btn--iconic header-btn-danger"
                    onClick={() => window.dispatchEvent(new CustomEvent('task-form-archive'))}
                    title="Archive this task — it leaves the list but is recoverable"
                  >
                    <Icon name="trash" size={16} />
                    <span className="header-action-btn__label">Archive</span>
                  </button>
                )}
              </>
            )}
          </>
        );
      }
      case 'vendor-suggestion':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('vendors')}>
              Cancel
            </button>
          </>
        );
      case 'song-new':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save Song">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save Song</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('music')}>
              Cancel
            </button>
          </>
        );
      case 'driver-new':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save Driver">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save Driver</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('logistics')}>
              Cancel
            </button>
          </>
        );
      case 'budget-category-new':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('budget')}>
              Cancel
            </button>
          </>
        );
      case 'menu-item-new':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('menu')}>
              Cancel
            </button>
          </>
        );
      case 'menu-item-edit':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave} title="Save Changes">
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">Save Changes</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('menu')}>
              Cancel
            </button>
            <div className="header-divider" />
            <button className="header-action-btn header-action-btn--iconic header-btn-danger" onClick={onFormDelete} title="Archive this item">
              <Icon name="trash" size={16} />
              <span className="header-action-btn__label">Archive</span>
            </button>
          </>
        );
      case 'calendar-event-new':
      case 'calendar-event-edit':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave}>
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">{activeView === 'calendar-event-edit' ? 'Save Changes' : 'Save'}</span>
            </button>
            <button className="header-action-btn" onClick={() => onViewChange('calendar')}>
              Cancel
            </button>
          </>
        );
      case 'settings':
        return (
          <>
            {settingsHasChanges && <span className="header-unsaved-text">Unsaved changes</span>}
            <button className="header-action-btn" onClick={onResetSettings} disabled={settingsSaving}>
              Reset to Defaults
            </button>
            <button className="header-action-btn primary" onClick={onSaveSettings} disabled={!settingsHasChanges || settingsSaving}>
              {settingsSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </>
        );

      case 'calendar':
        return (
          <>
            <div className="cal-toolbar-left">
              <button className="cal-nav-btn" onClick={onCalendarPrev}><Icon name="chevronLeft" size={16} /></button>
              <button className="cal-nav-btn" onClick={onCalendarNext}><Icon name="chevronRight" size={16} /></button>
              <button className="cal-today-btn" onClick={onCalendarToday}>Today</button>
              <span className="cal-period-label">{calendarLabel}</span>
            </div>
            <div className="header-toolbar-tabs">
              <button className={`header-toolbar-tab${calendarViewMode === 'week' ? ' active' : ''}`} onClick={() => onCalendarViewModeChange?.('week')}>Week</button>
              <button className={`header-toolbar-tab${calendarViewMode === 'month' ? ' active' : ''}`} onClick={() => onCalendarViewModeChange?.('month')}>Month</button>
            </div>
            <SplitButton
              label="New"
              mainLabel="New Task"
              icon="plus"
              primary
              onClick={onAddTask || (() => showInfo('Creating new task...'))}
              menuLabel="More ways to create"
              menu={[{ key: 'event', label: 'New Event', icon: 'calendar', onClick: onAddEvent || (() => showInfo('Creating new event...')) }]}
            />
            <IconButton icon="timeline" label="Timeline" onClick={() => onViewChange('timeline')} />
            <ExportButton exportKey="calendar" />
          </>
        );

      case 'venue':
        return (
          <VenuesListBar
            archived={isArchiveView(venueStatusFilter)}
            onArchivedChange={on => onVenueStatusFilterChange?.(toArchiveView(on))}
          />
        );

      case 'venue-detail':
        return (
          <>
            {canEdit ? (
              <>
                <div className="header-status-toggle">
                  <button
                    className={`header-status-btn${!venueArchived ? ' active' : ''}`}
                    onClick={() => { setVenueArchived(false); window.dispatchEvent(new CustomEvent('venue-status-set', { detail: false })); }}
                  >Active</button>
                  <button
                    className={`header-status-btn${venueArchived ? ' archived' : ''}`}
                    onClick={() => { setVenueArchived(true); window.dispatchEvent(new CustomEvent('venue-status-set', { detail: true })); }}
                  >Archived</button>
                </div>
                <div className="header-divider" />
                <button
                  className="header-action-btn header-action-btn--iconic primary"
                  onClick={() => window.dispatchEvent(new CustomEvent('venue-save-trigger'))}
                 title="Save Changes">
                  <Icon name="check" size={16} />
                  <span className="header-action-btn__label">Save Changes</span>
                </button>
                <IconButton icon="settings" label="Calibrate scale" onClick={() => window.dispatchEvent(new CustomEvent('floor-plan-calibrate'))} />
                <IconButton icon="folder" label="Save this layout as a reusable template" onClick={() => window.dispatchEvent(new CustomEvent('floor-plan-save-template-trigger'))} />
                <button
                  className="header-action-btn"
                  onClick={() => window.dispatchEvent(new CustomEvent('floor-plan-import'))}
                  title="Import a previously exported Anojeje floor plan SVG"
                >
                  <Icon name="upload" size={16} />
                </button>
              </>
            ) : (
              <span className={`header-status-btn${venueArchived ? ' archived' : ' active'}`}>{venueArchived ? 'Archived' : 'Active'}</span>
            )}
            <button
              className="header-action-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('venue-copy-link'))}
              title="Copy shareable link"
            >
              <Icon name="link" size={16} />
            </button>
            <button
              className="header-action-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('venue-export-png'))}
              title="Export floor plan as SVG"
            >
              <Icon name="download" size={16} />
            </button>
            {/* Destructive action sits last, after a divider — the same
                position it holds on guest, vendor, vehicle and task. */}
            {canEdit && venueArchived && (
              <>
                <div className="header-divider" />
                <button
                  className="header-action-btn header-action-btn--iconic header-btn-danger"
                  onClick={() => window.dispatchEvent(new CustomEvent('venue-delete-trigger'))}
                  title="Permanently delete this venue"
                >
                  <Icon name="trash" size={16} />
                  <span className="header-action-btn__label">Delete</span>
                </button>
              </>
            )}
          </>
        );

      case 'shipping':
        return (
          <>
            <AgentsListBar />
            <IconButton icon="wallet" label="Open Budget" onClick={() => navigate('/budget?category=Logistics')} />
          </>
        );

      case 'payment-record':
        return (
          <>
            {!isPaymentLocked && (
              <button
                className="header-action-btn header-action-btn--iconic primary"
                onClick={() => window.dispatchEvent(new CustomEvent('payment-record-save'))}
              >
                <Icon name="check" size={16} />
                <span className="header-action-btn__label">{isEditingPayment ? 'Save Changes' : 'Save Payment'}</span>
              </button>
            )}
            <button
              className="header-action-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('payment-record-cancel'))}
            >
              Cancel
            </button>
            {isEditingPayment && (
              <button
                className="header-action-btn header-action-btn--iconic header-btn-danger"
                onClick={() => window.dispatchEvent(new CustomEvent('payment-record-void'))}
                title="Void this payment — it will no longer count toward totals"
              >
                <Icon name="trash" size={16} /> <span className="header-action-btn__label">Void</span>
              </button>
            )}
          </>
        );

      case 'contributor-form':
        return (
          <>
            <button
              className="header-action-btn header-action-btn--iconic primary"
              onClick={() => window.dispatchEvent(new CustomEvent('contributor-form-save'))}
            >
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">{isEditingContributor ? 'Save Changes' : 'Add Contributor'}</span>
            </button>
            <button
              className="header-action-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('contributor-form-cancel'))}
            >
              Cancel
            </button>
            {isEditingContributor && (
              <>
                <div className="header-divider" />
                <button
                  className="header-action-btn header-action-btn--iconic header-btn-danger"
                  onClick={() => window.dispatchEvent(new CustomEvent('contributor-form-delete'))}
                  title="Archive this contributor"
                >
                  <Icon name="trash" size={16} />
                  <span className="header-action-btn__label">Archive</span>
                </button>
              </>
            )}
          </>
        );

      case 'payments':
        return (
          <>
            {paymentsTab === 'payments' && (
              <>
                {/* Was gated behind a separate "Filter" toggle button —
                    redundant with the dropdown itself, which already reads
                    "All Events" when nothing's selected. Just show it. */}
                <PaymentsFilters
                  event={paymentsEventFilter} onEventChange={v => onPaymentsEventFilterChange?.(v)}
                  status={paymentFilterStatus} onStatusChange={v => onPaymentFilterStatusChange?.(v)}
                  events={budgetEvents}
                />
                <button
                  className="header-action-btn header-action-btn--iconic primary"
                  onClick={() => window.dispatchEvent(new CustomEvent('payment-record-new'))}
                 title="Record Payment">
                  <Icon name="plus" size={16} />
                  <span className="header-action-btn__label">Record Payment</span>
                </button>
                <ExportButton exportKey="payments" />
              </>
            )}
            {paymentsTab === 'contributors' && (
              <>
                <div className="header-filter-group">
                  <ArchivedToggle
                    checked={isArchiveView(contributorArchiveFilter)}
                    onChange={on => setContributorArchiveFilter(toArchiveView(on))}
                  />
                </div>
                <button
                  className="header-action-btn header-action-btn--iconic primary"
                  onClick={() => navigate('/payments/contributor/new')}
                 title="New Contributor">
                  <Icon name="plus" size={16} />
                  <span className="header-action-btn__label">New Contributor</span>
                </button>
                <ExportButton exportKey="contributors" />
              </>
            )}
            {paymentsTab === 'settlement' && (
              <>
                <IconButton icon="document" label="Export settlement report" onClick={() => window.dispatchEvent(new CustomEvent('settlement-export-report'))} />
                <IconButton icon="download" label="Export settlement data as CSV" onClick={() => window.dispatchEvent(new CustomEvent('settlement-export-csv'))} />
              </>
            )}
          </>
        );

      case 'event-day':
        return null;

      case 'quote-new':
        return (
          <>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={() => window.dispatchEvent(new CustomEvent('quote:save'))} title="Save Quote">
              <Icon name="check" size={16} /> <span className="header-action-btn__label">Save Quote</span>
            </button>
            <button className="header-action-btn" onClick={() => window.dispatchEvent(new CustomEvent('quote:cancel-new'))}>
              Cancel
            </button>
          </>
        );

      case 'quotes':
        if (quoteNewOpen) {
          return (
            <>
              <button className="header-action-btn header-action-btn--iconic primary" onClick={() => window.dispatchEvent(new CustomEvent('quote:save'))} title="Save Quote">
                <Icon name="check" size={16} /> <span className="header-action-btn__label">Save Quote</span>
              </button>
              <button className="header-action-btn" onClick={() => window.dispatchEvent(new CustomEvent('quote:cancel-new'))}>
                Cancel
              </button>
            </>
          );
        }
        return (
          <>
            <div className="header-toolbar-tabs">
              <button
                className={`header-toolbar-tab${quotesViewMode === 'table' ? ' active' : ''}`}
                onClick={() => { setQuotesViewMode('table'); window.dispatchEvent(new CustomEvent('quote:view-mode', { detail: 'table' })); }}
                title="Table view"
              >
                <Icon name="list" size={14} />
              </button>
              <button
                className={`header-toolbar-tab${quotesViewMode === 'grid' ? ' active' : ''}`}
                onClick={() => { setQuotesViewMode('grid'); window.dispatchEvent(new CustomEvent('quote:view-mode', { detail: 'grid' })); }}
                title="Card view"
              >
                <Icon name="grid" size={14} />
              </button>
            </div>
            <QuotesFilters
              status={quoteStatusFilter}
              onStatusChange={v => { setQuoteStatusFilter(v); window.dispatchEvent(new CustomEvent('quote:status-filter-change', { detail: v })); }}
              category={quoteCategoryFilter}
              onCategoryChange={v => { setQuoteCategoryFilter(v); window.dispatchEvent(new CustomEvent('quote:category-filter-change', { detail: v })); }}
              categories={quoteCategories}
            />
            <button className="header-action-btn header-action-btn--iconic primary" onClick={() => window.dispatchEvent(new CustomEvent('quote:new'))} title="New Quote">
              <Icon name="plus" size={16} /> <span className="header-action-btn__label">New Quote</span>
            </button>
            <ExportButton exportKey="quotes" />
          </>
        );


      case 'quote-detail': {
        const decisionLabel = quoteDetailStatus === 'rejected' ? 'Rejected' : quoteDetailStatus === 'accepted' ? 'Accepted' : 'Decision';
        const quoteSteps: HeaderStep[] = [
          { key: 'added', label: 'Quote Added' },
          { key: 'pending', label: 'Pending Review', tone: 'active' },
          { key: 'decision', label: decisionLabel, tone: quoteDetailStatus === 'rejected' ? 'failed' : 'done' },
        ];
        const quoteTracker = (
          <HeaderStatusSteps
            ariaLabel="Quote status"
            steps={quoteSteps}
            currentIndex={quoteDetailStatus === 'pending' ? 1 : 2}
          />
        );

        if (quoteDetailStatus !== 'pending') {
          return (
            <>
              {quoteTracker}
              <span className="header-btn-divider" aria-hidden="true" />
              <ExportButton exportKey="quotes" />
              <button
                className="header-action-btn header-action-btn--iconic header-btn-danger"
                onClick={() => window.dispatchEvent(new CustomEvent('quote-detail:delete'))}
                title="Delete this quote"
              >
                <Icon name="trash" size={16} /> <span className="header-action-btn__label">Delete</span>
              </button>
            </>
          );
        }
        // Accept/Reject moved into the page's own sidebar (QuotesTracker.tsx,
        // "Quote Details" panel) — they're irreversible decisions, not field
        // edits, so they stay separate from the Save button here. Save
        // commits the draft Category/Events/Date Received/Notes edits in one
        // shot (see quote:save-edit below), matching the rest of the app's
        // explicit-save convention (the forms/*FormPage set's onFormSave).
        return (
          <>
            {quoteTracker}
            <span className="header-btn-divider" aria-hidden="true" />
            <button
              className="header-action-btn header-action-btn--iconic primary"
              onClick={() => window.dispatchEvent(new CustomEvent('quote:save-edit'))}
              disabled={!quoteDetailDirty}
             title="Save">
              <Icon name="check" size={16} /> <span className="header-action-btn__label">Save</span>
            </button>
            <button
              className="header-action-btn header-action-btn--iconic header-btn-danger"
              onClick={() => window.dispatchEvent(new CustomEvent('quote-detail:delete'))}
              title="Delete this quote"
            >
              <Icon name="trash" size={16} /> <span className="header-action-btn__label">Delete</span>
            </button>
          </>
        );
      }


      case 'catalogue':
        return (
          <>
            <div className="header-filter-group">
              <ArchivedToggle
                checked={isArchiveView(catalogArchivedFilter)}
                onChange={on => {
                  const v = toArchiveView(on);
                  setCatalogArchivedFilter(v);
                  window.dispatchEvent(new CustomEvent('catalog-archived-set', { detail: v }));
                }}
              />
            </div>
            <ExportButton exportKey="catalogue" />
            {/* Was a standalone button below the metric cards on the page
                itself — every other list page's primary action lives here
                in the breadcrumb actions row instead. */}
            <button className="header-action-btn header-action-btn--iconic primary" onClick={() => navigate('/catalog/new')} title="Add Item">
              <Icon name="plus" size={16} />
              <span className="header-action-btn__label">Add Item</span>
            </button>
          </>
        );

      case 'catalogue-new':
      case 'catalogue-edit':
        return (
          <>
            <button
              className={`header-action-btn header-action-btn--iconic${catalogCustomizable ? ' active' : ''}`}
              onClick={() => {
                const next = !catalogCustomizable;
                setCatalogCustomizable(next);
                window.dispatchEvent(new CustomEvent('catalog-customizable-set', { detail: next }));
              }}
              title={catalogCustomizable ? 'Guests can personalize this item — click to turn off' : 'Guests cannot personalize this item — click to allow'}
            >
              <Icon name="edit" size={16} />
              <span className="header-action-btn__label">{catalogCustomizable ? 'Customizable' : 'Not customizable'}</span>
            </button>
            <button className="header-action-btn header-action-btn--iconic primary" onClick={onFormSave}>
              <Icon name="check" size={16} />
              <span className="header-action-btn__label">{activeView === 'catalogue-edit' ? 'Save Changes' : 'Save Item'}</span>
            </button>
            <button className="header-action-btn" onClick={() => navigate('/catalogue')}>
              Cancel
            </button>
            {activeView === 'catalogue-edit' && (
              <>
                <div className="header-divider" />
                <button className="header-action-btn header-action-btn--iconic header-btn-danger" onClick={onFormDelete} title="Archive this item">
                  <Icon name="trash" size={16} />
                  <span className="header-action-btn__label">Archive</span>
                </button>
              </>
            )}
          </>
        );


      default:
        return null;
    }
  };

  // ── Breadcrumb logic ──────────────────────────────────────────────────────

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    switch (activeView) {
      case 'dashboard':
        return [];
      case 'timeline':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Event Day Timeline', view: 'timeline' }];
      case 'asoebi-fabrics':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Asoebi & Style', view: 'asoebi' }, { label: 'Fabrics', view: 'asoebi-fabrics' }];
      case 'asoebi-orders':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Asoebi & Style', view: 'asoebi' }, { label: 'Orders', view: 'asoebi-orders' }];
      case 'asoebi-assignments': {
        const root: BreadcrumbItem[] = [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Asoebi & Style', view: 'asoebi' }, { label: 'Assignments', view: 'asoebi-assignments' }];
        const openId = parseIdFromPath('AZ', location.pathname, '/asoebi/assignments/');
        if (openId) return [...root, { label: recordLabel('asoebiAssignment', openId), view: 'asoebi-assignments' }];
        if (location.pathname.replace(/\/+$/, '').endsWith('/new')) return [...root, { label: 'New assignment', view: 'asoebi-assignments' }];
        return root;
      }
      case 'gift-registry':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Gift Registry', view: 'gift-registry' }];
      case 'gift-registry-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Gift Registry', view: 'gift-registry' }, { label: 'New item', view: 'gift-registry-new' }];
      case 'gift-registry-item': {
        const n = giftRefFromPath(location.pathname);
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Gift Registry', view: 'gift-registry' }, { label: n ? giftRefLabel(n) : 'Item', view: 'gift-registry-item' }];
      }
      case 'vendor-bookings':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Vendor Bookings', view: 'vendor-bookings' }];
      case 'vendor-booking-edit': {
        const openId = parseIdFromPath('BK', location.pathname, '/vendor-bookings/');
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Vendor Bookings', view: 'vendor-bookings' },
          { label: openId ? recordLabel('vendorBooking', openId) : 'Booking', view: 'vendor-booking-edit' },
        ];
      }
      case 'asoebi':
      case 'asoebi-bundles': {
        const crumbs: BreadcrumbItem[] = [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Asoebi & Style', view: 'asoebi' },
          { label: 'Bundles', view: 'asoebi-bundles' },
        ];
        // Reflects the /bundle/new or /bundle/:id URL (App.tsx) — the bundle
        // form is an inline panel, not a separate activeView, so this is
        // driven by the same bundle:form-open/-close events that already
        // power the header's Cancel/Save/Delete actions above.
        if (bundleFormOpen) {
          crumbs.push({ label: bundleIsEdit ? (bundleEditName || 'Edit Bundle') : 'New Bundle', view: 'asoebi-bundles' });
        }
        return crumbs;
      }
      case 'menu':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Menu', view: 'menu' },
          { label: menuTab || 'Traditional', view: 'menu' },
        ];
      case 'vendor-compare':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Vendors', view: 'vendors' },
          { label: 'Compare', view: 'vendor-compare' },
        ];
      case 'vendors':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Vendors', view: 'vendors' }];
      case 'guests': {
        const route = matchGuestsRoute(location.pathname);
        const base = [{ label: 'Dashboard', view: 'dashboard' as const }, { label: 'Guests', view: 'guests' as const }];
        if (route?.kind === 'new') return [...base, { label: 'New guest', view: 'guests' as const }];
        if (route?.kind === 'rsvps') return [...base, { label: 'RSVPs', view: 'guests' as const }];
        if (route?.kind === 'profile' || route?.kind === 'edit') return [...base, { label: route.ref, view: 'guests' as const }];
        return base;
      }
      case 'budget':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Budget', view: 'budget' }];
      case 'budget-category':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Budget', view: 'budget' },
          { label: budgetCategoryName || (budgetCategoryId ? recordLabelOr('budgetCategory', budgetCategoryId, `BC-${budgetCategoryId.padStart(3, '0')}`) : 'Category'), view: 'budget' },
          { label: tabLabel(BUDGET_CATEGORY_TABS, budgetCategoryTab, 'Overview'), view: 'budget-category' },
        ];
      case 'calculator':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Budget', view: 'budget' },
          { label: 'Budget Configurator', view: 'calculator' },
        ];
      case 'shipping':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Procurement', view: 'procurement-orders' }, { label: 'Shipping Agents', view: 'shipping' }];
      case 'venue':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Venues', view: 'venue' }];
      case 'calendar':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Calendar', view: 'calendar' }];
      case 'logistics': {
        const logisticsTabLabel = tabLabel(LOGISTICS_TABS, logisticsTab, 'Schedule');
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Logistics', view: 'logistics' },
          { label: logisticsTabLabel, view: 'logistics' },
        ];
      }
      case 'procurement': {
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Procurement', view: 'procurement-orders' },
          { label: 'Inventory', view: 'procurement' },
        ];
      }
      case 'procurement-orders':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Procurement', view: 'procurement-orders' }, { label: 'Orders', view: 'procurement-orders' }];
      case 'procurement-order-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Procurement', view: 'procurement-orders' }, { label: 'Orders', view: 'procurement-orders' }, { label: 'New Order', view: 'procurement-order-new' }];
      case 'procurement-order-edit':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Procurement', view: 'procurement-orders' }, { label: 'Orders', view: 'procurement-orders' }, { label: procurementBar.detail?.kind === 'order' ? procurementBar.detail.title : 'Order', view: 'procurement-order-edit' }];
      case 'procurement-shipments':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Procurement', view: 'procurement-orders' }, { label: 'Shipments', view: 'procurement-shipments' }];
      case 'procurement-shipment-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Procurement', view: 'procurement-orders' }, { label: 'Shipments', view: 'procurement-shipments' }, { label: 'New Shipment', view: 'procurement-shipment-new' }];
      case 'procurement-shipment-edit':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Procurement', view: 'procurement-orders' }, { label: 'Shipments', view: 'procurement-shipments' }, { label: procurementBar.detail?.kind === 'shipment' ? procurementBar.detail.title : 'Shipment', view: 'procurement-shipment-edit' }];
      case 'music':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Music', view: 'music' }];
      case 'activities':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Tasks', view: 'activities' }];
      case 'stationery':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Stationery & Branding', view: 'stationery' }];
      case 'decor':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Decor Planner', view: 'decor' }];
      case 'decor-new':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Decor Planner', view: 'decor' },
          { label: 'Add Element', view: 'decor-new' },
        ];
      case 'decor-edit':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Decor Planner', view: 'decor' },
          ...(decorElementCategory ? [{ label: decorElementCategory.charAt(0) + decorElementCategory.slice(1).toLowerCase(), view: 'decor' as const }] : []),
          { label: decorElementName || (decorElementId ? recordLabelOr('decorElement', decorElementId, `DC-${decorElementId.padStart(3, '0')}`) : 'Edit Element'), view: 'decor-edit' },
        ];
      case 'stationery-item-edit':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Stationery & Branding', view: 'stationery' },
          { label: stationeryItemName || (stationeryItemId ? recordLabelOr('stationeryItem', stationeryItemId, `ST-${stationeryItemId.padStart(3, '0')}`) : 'Edit Item'), view: 'stationery-item-edit' },
        ];
      case 'settings':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Settings', view: 'settings' }];
      case 'venue-detail': {
        const crumbs: { label: string; view: string }[] = [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Venues', view: 'venue' },
        ];
        if (fpVenueName) crumbs.push({ label: fpVenueName, view: 'venue-detail' });
        return crumbs;
      }
      case 'vendor-detail':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Vendors', view: 'vendors' },
          { label: vendorName || (vendorId ? recordLabelOr('vendor', vendorId, `VD-${vendorId.padStart(3, '0')}`) : 'Vendor'), view: 'vendor-detail' },
        ];
      case 'vendor-profile':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Vendors', view: 'vendors' },
          { label: vendorProfileName || (vendorProfileId ? recordLabelOr('vendor', vendorProfileId, `VD-${vendorProfileId.padStart(3, '0')}`) : 'Vendor'), view: 'vendor-profile' },
        ];
      case 'vendor-rating':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Vendors', view: 'vendors' },
          { label: vendorRatingName || (vendorRatingId ? recordLabelOr('vendor', vendorRatingId, `VD-${vendorRatingId.padStart(3, '0')}`) : 'Vendor'), view: 'vendor-rating' },
        ];
      case 'payments':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Payments', view: 'payments' },
          { label: tabLabel(PAYMENTS_TABS, paymentsTab, 'Overview'), view: 'payments' },
        ];
      case 'payment-record':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Payments', view: 'payments' },
          { label: isEditingPayment ? (editPaymentId ? recordLabelOr('payment', editPaymentId, `PY-${String(editPaymentId).padStart(3, '0')}`) : 'Edit Payment') : 'New Payment', view: 'payment-record' },
        ];
      case 'team-member-form':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Team', view: 'team' },
          { label: location.pathname.endsWith('/new') ? 'New team member' : 'Team member', view: 'team-member-form' },
        ];
      case 'contributor-form':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Payments', view: 'payments' },
          { label: isEditingContributor ? (contributorState?.name ?? (contributorId ? recordLabelOr('contributor', contributorId, `CN-${contributorId.padStart(3, '0')}`) : 'Edit Contributor')) : 'Add Contributor', view: 'contributor-form' },
        ];
      case 'event-day': {
        const edEvent = edEventId ? edEvents?.find(e => e.id === edEventId) : null;
        if (edEvent) {
          // Dashboard → {Event Name} ({Venue}) → [tab dropdown]
          return [
            { label: 'Dashboard', view: 'dashboard' },
            { label: edEvent.venue ? `${edEvent.title} — ${edEvent.venue}` : edEvent.title, view: 'event-day' },
            { label: 'Event Day Hub', view: 'event-day' },
          ];
        }
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Event Day Hub', view: 'event-day' }];
      }
      case 'task-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Tasks', view: 'activities' }, { label: 'New Task', view: 'task-new' }];
      case 'task-edit':
        // The id, not the task name: a long task name ("Create protocol plan
        // for arrival, greeting, escorting and seating of high-profile...")
        // truncates illegibly in the breadcrumb bar, and every other entity's
        // last crumb (AOS-xxx, AZ-xxx, TM-xxx) already shows its id rather
        // than its own long-form name for the same reason.
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Tasks', view: 'activities' }, { label: currentTaskId ? recordLabel('task', currentTaskId) : 'Task', view: 'task-edit' }];
      case 'vehicle-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Logistics', view: 'logistics' }, { label: 'New Vehicle', view: 'vehicle-new' }];
      case 'vehicle-detail':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Logistics', view: 'logistics' }, { label: vehicleName || (vehicleId ? recordLabelOr('vehicle', vehicleId, `VH-${vehicleId.padStart(3, '0')}`) : 'Vehicle'), view: 'vehicle-detail' }];
      case 'driver-detail':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Logistics', view: 'logistics' }, { label: driverName || (driverId ? recordLabelOr('driver', driverId, `DR-${driverId.padStart(3, '0')}`) : 'Driver'), view: 'driver-detail' }];
      case 'stationery-item-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Stationery & Branding', view: 'stationery' }, { label: 'New Item', view: 'stationery-item-new' }];
      case 'budget-category-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Budget', view: 'budget' }, { label: 'New Category', view: 'budget-category-new' }];
      case 'song-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Music', view: 'music' }, { label: 'New Song', view: 'song-new' }];
      case 'driver-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Logistics', view: 'logistics' }, { label: 'New Driver', view: 'driver-new' }];
      case 'menu-item-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Menu', view: 'menu' }, { label: menuTab || 'Traditional', view: 'menu' }, { label: 'New Item', view: 'menu-item-new' }];
      case 'menu-item-edit':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Menu', view: 'menu' }, { label: menuTab || 'Traditional', view: 'menu' }, { label: menuItemName || (menuItemId ? recordLabelOr('menuItem', menuItemId, `MI-${menuItemId.padStart(3, '0')}`) : 'Edit Item'), view: 'menu-item-edit' }];
      case 'events':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Events', view: 'events' }];
      case 'event-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Events', view: 'events' }, { label: 'New Event', view: 'event-new' }];
      case 'event-detail':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Events', view: 'events' }, { label: eventsBar.detail?.title || 'Event', view: 'event-detail' }];
      case 'guidelines':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Guidelines', view: 'guidelines' }];
      case 'guideline-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Guidelines', view: 'guidelines' }, { label: 'New Guideline', view: 'guideline-new' }];
      case 'guideline-edit':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Guidelines', view: 'guidelines' }, { label: guidelineName || (guidelineId ? recordLabelOr('guideline', guidelineId, `GL-${guidelineId.padStart(3, '0')}`) : 'Edit Guideline'), view: 'guideline-edit' }];
      case 'calendar-event-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Calendar', view: 'calendar' }, { label: 'New Event', view: 'calendar-event-new' }];
      case 'calendar-event-edit':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Calendar', view: 'calendar' }, { label: 'Edit Event', view: 'calendar-event-edit' }];
      case 'vendor-suggestion':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Vendors', view: 'vendors' }, { label: 'Add Vendor', view: 'vendor-suggestion' }];
      case 'quote-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Quotes', view: 'quotes' }, { label: 'New Quote', view: 'quote-new' }];
      case 'budget-variances':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Budget', view: 'budget' }, { label: 'Variances', view: 'budget-variances' }];
      case 'quotes':
        return quoteNewOpen
          ? [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Quotes', view: 'quotes' }, { label: 'New Quote', view: 'quotes' }]
          : [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Quotes', view: 'quotes' }];
      case 'quote-detail':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Quotes', view: 'quotes' },
          { label: quoteDetailId ? recordLabelOr('quote', quoteDetailId, `QT-${quoteDetailId.padStart(3, '0')}`) : 'Quote', view: 'quote-detail' },
        ];
      case 'shipping-agent-edit':
        return [
          { label: 'Dashboard', view: 'dashboard' },
          { label: 'Procurement', view: 'procurement-orders' },
          { label: 'Shipping Agents', view: 'shipping' },
          { label: shippingAgentPanelLabel || 'Agent', view: 'shipping-agent-edit' },
        ];
      case 'catalogue':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Catalogue', view: 'catalogue' }];
      case 'catalogue-new':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Catalogue', view: 'catalogue' }, { label: 'New Item', view: 'catalogue-new' }];
      case 'catalogue-edit':
        return [{ label: 'Dashboard', view: 'dashboard' }, { label: 'Catalogue', view: 'catalogue' }, { label: catalogueItemName || (catalogueItemId ? recordLabelOr('catalogueItem', catalogueItemId, `CT-${catalogueItemId.padStart(3, '0')}`) : 'Edit Item'), view: 'catalogue-edit' }];
      default:
        return [{ label: 'Dashboard', view: 'dashboard' }];
    }
  };

  const breadcrumbs = getBreadcrumbs();
  const pageIcon = VIEW_ICONS[activeView] || 'chart';
  const pageTitle =
    activeView === 'stationery-item-edit' && stationeryItemName
      ? stationeryItemName
      : activeView === 'catalogue-edit' && catalogueItemName
      ? catalogueItemName
      : activeView === 'menu-item-edit' && menuItemName
      ? menuItemName
      : activeView === 'event-detail' && eventsBar.detail?.title
      ? eventsBar.detail.title
      : activeView === 'procurement-order-edit' && procurementBar.detail?.kind === 'order'
      ? procurementBar.detail.title
      : activeView === 'procurement-shipment-edit' && procurementBar.detail?.kind === 'shipment'
      ? procurementBar.detail.title
      : activeView === 'guideline-edit' && guidelineName
      ? guidelineName
      : activeView === 'budget-category' && budgetCategoryName
      ? `Budget: ${budgetCategoryName}`
      : VIEW_TITLES[activeView] || 'Dashboard';
  const actionButtons = getActionButtons();

  // Back button navigates to second-to-last breadcrumb

  // Show breadcrumb bar when there are breadcrumbs to display or there are contextual actions
  const showBreadcrumbBar = breadcrumbs.length > 1 || actionButtons !== null;

  return (
    <>
      {/* ── Top header bar — hidden in Event Day fullscreen mode; the
          breadcrumb sub-bar below (with the exit-fullscreen button) stays
          visible so this is never a one-way trap ──────────────────────── */}
      {!edFullscreen && (
      <header className="global-header">
        {/* Left: hamburger (mobile) + page icon + page title */}
        <div className="global-header-left">
          <button
            className="global-header-hamburger"
            onClick={onMobileMenuToggle}
            aria-label="Open navigation menu"
          >
            <span /><span /><span />
          </button>
          <div className="global-header-page-icon">
            <Icon name={pageIcon} size={20} />
          </div>
          <h1 className="global-header-title">{pageTitle}</h1>
        </div>

        {/* Right: workspace switcher + search + refresh + notifications + user */}
        <div className="global-header-right">
          <DemoBadge />
          <WorkspaceSwitcher />
          <GlobalSearch />

          <GlobalRefreshButton className="header-refresh-standalone" onRefresh={handleGlobalSync} busy={isSyncing} title="Refresh all data" />

          {/* Below 640px the standalone refresh button (above) is hidden by
              CSS and this overflow menu takes its place, freeing width for
              the page title next to search/notifications/avatar. */}
          <details className="header-more-menu">
            <summary className="global-header-icon-btn" aria-label="More actions" title="More actions">
              <Icon name="moreVertical" size={20} />
            </summary>
            <div className="header-more-dropdown">
              <button
                type="button"
                className={`header-more-item${isSyncing ? ' syncing' : ''}`}
                onClick={handleGlobalSync}
                disabled={isSyncing}
              >
                <Icon name="refresh" size={16} />
                {isSyncing ? 'Refreshing…' : 'Refresh all data'}
              </button>
            </div>
          </details>

          <div className="notification-bell-container">
            <button
              className="global-header-icon-btn"
              onClick={() => setIsNotificationDropdownOpen(!isNotificationDropdownOpen)}
              title={`${unreadCount > 0 ? unreadCount + ' unread ' : ''}notifications`}
              aria-label="Notifications"
            >
              <Icon name="bell" size={20} />
              {unreadCount > 0 && (
                <span className="global-header-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </button>

            <NotificationDropdown
              isOpen={isNotificationDropdownOpen}
              onClose={() => setIsNotificationDropdownOpen(false)}
            />
          </div>

          <div className="user-profile-container">
            <AuthButton />
          </div>
        </div>
      </header>
      )}

      {/* ── Breadcrumb sub-bar ──────────────────────────────────────────── */}
      {showBreadcrumbBar && (
        <div className={`global-breadcrumb-bar${edFullscreen ? ' global-breadcrumb-bar--fullscreen' : ''}`}>
          {/* Left: breadcrumb trail */}
          <div className="global-breadcrumb-left">
            {breadcrumbs.length > 1 && (
              <>
              <nav className="global-breadcrumb-trail" aria-label="Breadcrumb">

                {breadcrumbs.map((crumb, index) => {
                  // On narrow screens, collapse straight to "… / Last" —
                  // there's no room for even the first crumb alongside
                  // whatever filter/view control sits in the same row.
                  if (isNarrowBreadcrumb && breadcrumbs.length > 1 && index < breadcrumbs.length - 1) return null;
                  // Truncate: when > 3 crumbs, hide middle items; show … before second-to-last
                  if (!isNarrowBreadcrumb && breadcrumbs.length > 3 && index > 0 && index < breadcrumbs.length - 2) return null;
                  return (
                  <React.Fragment key={`${crumb.view}-${index}`}>
                    {index > 0 && !isNarrowBreadcrumb && (
                      <span className="global-breadcrumb-separator" aria-hidden="true">/</span>
                    )}
                    {isNarrowBreadcrumb && breadcrumbs.length > 1 && index === breadcrumbs.length - 1 && (
                      <>
                        <span className="global-breadcrumb-ellipsis" aria-hidden="true">…</span>
                        <span className="global-breadcrumb-separator" aria-hidden="true">/</span>
                      </>
                    )}
                    {!isNarrowBreadcrumb && breadcrumbs.length > 3 && index === breadcrumbs.length - 2 && (
                      <>
                        <span className="global-breadcrumb-ellipsis" aria-hidden="true">…</span>
                        <span className="global-breadcrumb-separator" aria-hidden="true">/</span>
                      </>
                    )}
                    {index === breadcrumbs.length - 1 && activeView === 'vendor-detail' && vendorList && vendorList.length > 0 ? (
                      // Was matched by name string (v.name === crumb.label) —
                      // any mismatch (vendorList not yet loaded, a subtle
                      // string difference) made a controlled <select> with no
                      // matching option silently fall back to displaying its
                      // first option, which read as "wrong vendor in the
                      // breadcrumb" even though the page itself had the right
                      // one. vendorId is already the real, unambiguous value.
                      <SearchableSelect
                        className="global-breadcrumb-dropdown"
                        value={vendorId ?? ''}
                        onChange={v => onVendorNavigate?.(v)}
                        options={[...vendorList].sort((a, b) => a.name.localeCompare(b.name)).map(v => ({ value: v.id, label: v.name }))}
                      />
                    ) : index === breadcrumbs.length - 1 && activeView === 'menu' && !!onMenuTabChange ? (
                      <SearchableSelect
                        className="global-breadcrumb-dropdown"
                        value={menuTab ?? 'Traditional'}
                        onChange={v => onMenuTabChange(v)}
                        options={MENU_TABS.map(t => ({ value: t, label: t }))}
                      />
                    ) : index === breadcrumbs.length - 1 && activeView === 'budget-category' && !!onBudgetCategoryTabChange ? (
                      <SearchableSelect
                        className="global-breadcrumb-dropdown"
                        value={budgetCategoryTab}
                        onChange={v => onBudgetCategoryTabChange(v as 'overview' | 'vendors' | 'payments')}
                        options={BUDGET_CATEGORY_DROPDOWN_TABS}
                      />
                    ) : index === breadcrumbs.length - 1 && activeView === 'payments' && !!onPaymentsTabChange ? (
                      <SearchableSelect
                        className="global-breadcrumb-dropdown"
                        value={paymentsTab}
                        onChange={v => onPaymentsTabChange(v as 'payments' | 'contributors' | 'settlement')}
                        options={PAYMENTS_TABS}
                      />
                    ) : index === breadcrumbs.length - 1 && activeView === 'logistics' && !!onLogisticsTabChange ? (
                      <SearchableSelect
                        className="global-breadcrumb-dropdown"
                        value={logisticsTab}
                        onChange={v => onLogisticsTabChange(v)}
                        options={LOGISTICS_TABS}
                      />
                    ) : index === breadcrumbs.length - 1 && activeView === 'event-day' && onEdTabChange ? (
                      <>
                        <SearchableSelect
                          className="global-breadcrumb-dropdown"
                          value={edTab ?? 'overview'}
                          onChange={v => onEdTabChange(v)}
                          options={EVENT_DAY_TABS}
                        />
                        {edTab === 'waiter' && onEdWaiterModeChange && (
                          <>
                            <span className="global-breadcrumb-separator" aria-hidden="true">/</span>
                            <SearchableSelect
                              className="global-breadcrumb-dropdown"
                              value={edWaiterMode ?? 'orders'}
                              onChange={v => onEdWaiterModeChange(v as 'orders' | 'assignments')}
                              options={[
                                { value: 'orders', label: 'Orders' },
                                { value: 'assignments', label: 'Assignments' },
                              ]}
                            />
                          </>
                        )}
                      </>
                    ) : index === breadcrumbs.length - 1 ? (
                      <span className="global-breadcrumb-current" aria-current="page">
                        {crumb.label}
                      </span>
                    ) : activeView === 'event-day' && index === 1 && edEvents && edEvents.length > 0 && onEdEventChange ? (
                      <SearchableSelect
                        className="global-breadcrumb-dropdown"
                        value={edEventId ?? ''}
                        onChange={v => onEdEventChange(v)}
                        options={edEvents.map(e => ({ value: e.id, label: e.title }))}
                      />
                    ) : (
                      <button
                        className="global-breadcrumb-link"
                        onClick={() => crumb.onAction
                          ? crumb.onAction()
                          : onViewChange(crumb.view, crumb.eventId ? { eventId: crumb.eventId } : undefined)
                        }
                      >
                        {crumb.label}
                      </button>
                    )}
                  </React.Fragment>
                  );
                })}
                {/* Inside the trail, not after it: .global-breadcrumb-trail
                    is flex:1 and grows to fill the bar, so a sibling placed
                    after it was pushed to the far right edge instead of
                    sitting beside the crumb that names the record. */}
                {/* Previous / next record of the list the user came from (useRecordNav). */}
                <RecordNav />
              </nav>
              </>
            )}
          </div>

          {/* Right: contextual action buttons */}

          {(actionButtons || (isEventDay && activeView !== 'event-day') || (activeView === 'event-day' && (edTab === 'overview' || edTab === 'kitchen' || edTab === 'bar' || (edTab === 'waiter' && !!waiterMenu)))) && (
            <div className="global-breadcrumb-actions">
              {actionButtons}
              {activeView === 'event-day' && edTab === 'waiter' && waiterMenu && (
                <SearchableSelect
                  className="header-filter-select"
                  ariaLabel="Menu category"
                  value={waiterMenu.active}
                  onChange={value => window.dispatchEvent(new CustomEvent(WAITER_MENU_TAB_SET_EVENT, { detail: value }))}
                  options={waiterMenu.tabs.map(tab => ({ value: tab, label: tab }))}
                />
              )}
              {activeView === 'event-day' && edTab === 'overview' && (
                <div className="ed-toolbar-group">
                  <span className="ed-clock">
                    <Clock size={13} />
                    {edClock.toTimeString().slice(0, 8)}
                  </span>
                  <span className={`header-live-badge header-live-badge--${edConnected === null ? 'pending' : edConnected ? 'live' : 'offline'}`}>
                    <Icon name={edConnected === false ? 'wifi-off' : 'wifi'} size={13} />
                    {edConnected === null ? 'Connecting…' : edConnected ? 'Live' : 'Offline'}
                  </span>
                  {/* Phase stepper — read-only display, shows prev / current / next only */}
                  <div className="ed-phase-pills">
                    {['Arrival', 'Cocktail Hour', 'Entrance', 'Introductions', 'Dinner', 'Cake Cutting', 'Dance Floor', 'Farewell']
                      .map((name, i) => ({ name, i }))
                      .filter(({ i }) => Math.abs(i - edPhase) <= 1)
                      .map(({ name, i }) => (
                        <span
                          key={i}
                          className={`ed-phase-pill${i === edPhase ? ' active' : i < edPhase ? ' done' : ''}`}
                        >{name}</span>
                      ))}
                  </div>
                  <div className="ed-system-status-wrap" ref={systemStatusRef}>
                    <button
                      className={`ed-heatmap-btn${systemStatusOpen ? ' active' : ''}`}
                      onClick={() => setSystemStatusOpen(v => !v)}
                    >
                      <Zap size={13} /> Status
                    </button>
                    {systemStatusOpen && <SystemStatusPopover connected={edConnected} />}
                  </div>
                  <button
                    type="button"
                    className={`ed-heatmap-btn${edFullscreen ? ' active' : ''}`}
                    onClick={() => {
                      const next = !edFullscreen;
                      setEdFullscreen(next);
                      window.dispatchEvent(new CustomEvent('event-day-fullscreen', { detail: next }));
                    }}
                    title={edFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  >
                    {edFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                  </button>
                </div>
              )}
              {/* Kanban / Batch toggle — kitchen + bar tabs */}
              {activeView === 'event-day' && (edTab === 'kitchen' || edTab === 'bar') && onEdKbViewModeChange && (
                <div className="ed-kb-toggle">
                  <button className={`ed-kb-btn${edKbViewMode === 'kanban' ? ' active' : ''}`} onClick={() => onEdKbViewModeChange('kanban')}>
                    <LayoutList size={13} /> Kanban
                  </button>
                  <button className={`ed-kb-btn${edKbViewMode === 'batch' ? ' active' : ''}`} onClick={() => onEdKbViewModeChange('batch')}>
                    <Layers size={13} /> Batch
                  </button>
                </div>
              )}
              {isEventDay && activeView !== 'event-day' && (
                <button
                  className="header-action-btn header-action-btn--iconic header-eventday-live"
                  onClick={() => onViewChange('event-day')}
                  title="Event day is today — open Event Day Hub"
                >
                  <Icon name="activity" size={16} />
                  <span className="header-action-btn__label">Event Day</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Header;
